import React, { useRef, useEffect, useLayoutEffect, useState, useMemo, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { getVesselIconComponent } from '../../utils/vesselIcons';
import { buildDemoVesselIconLayers } from '../../utils/demoVesselMapIcons';
import {
  vesselIconPixelSizeForMapZoom,
  vesselThreatIconPixelSizeForMapZoom,
  vesselDotRadiusPixelsForMapZoom
} from '../../utils/vesselMarkerZoomScale';
import { resolveVesselMaritimeHeadingDeg } from '../../utils/vesselMapIconAngle';
import { isDemoThreatMissileVessel } from '../../utils/demoThreatMissileVessel';
import { isDemoAuvRasterVessel } from '../../utils/demoAuvRasterVessel';
import DeckGL from '@deck.gl/react';
import { PathStyleExtension } from '@deck.gl/extensions';
import { ScatterplotLayer, LineLayer, IconLayer, PolygonLayer, PathLayer } from '@deck.gl/layers';
import { HeatmapLayer, HexagonLayer } from '@deck.gl/aggregation-layers';
// TileLayer, BitmapLayer, GeoJsonLayer removed as unused
import StationTooltip from './StationTooltip';
import SelectedStationPanel from './SelectedStationPanel';
import { isLikelyOnWater } from '../../services/dataService';
// Arrow icon will be created programmatically
import 'mapbox-gl/dist/mapbox-gl.css';

// Improved Wind Particle Layer with better visibility
import { CompositeLayer } from '@deck.gl/core';
import { generateTrackLayer, buildTrackSegmentsFromTracks } from './TrackLayer';
import { generateMarkerClusterLayer } from './MarkerClusterLayer';
import ThreatZoneOverlay from './ThreatZoneOverlay';
import PulsingThreatLayer from './PulsingThreatLayer';
import { generateVesselLayer } from './VesselLayer';
import { generateTrailingPathLayer } from './TrailingPathLayer';
import VesselHUD from './VesselHUD';
import { sortVesselData, clusterVesselData, interpolateMultipleVesselStates, vesselHasTravelPath, VESSEL_DISPLAY_NONE } from '../../utils/vesselUtils';
import {
  getRowTimeMs,
  sortedExerciseStoryRowsFromRaw,
  isVesselMapPlaybackViewMode
} from '../../utils/storyTimeUtils';
import { buildExerciseVesselPathsFromRawData } from '../../utils/exerciseVesselPaths';
import {
  SCENARIO_POLYGON_FEATURES,
  EXERCISE_SCENARIO_BOUNDS,
  EXERCISE_OP_ALPHA_PROXIMITY_THREAT_VESSEL_ID,
  EXERCISE_OP_ALPHA_PROXIMITY_BUFFER_M,
  EXERCISE_THREAT_DEFENSE_CONVERGENCE_MAX_M,
  EXERCISE_THREAT_DEFENSE_CONVERGENCE_RING_RADIUS_M
} from '../../data/realisticExerciseScenarioData';
import {
  isVesselNearOpAlphaThreatZone,
  haversineDistanceMeters
} from '../../utils/exerciseOpAlphaProximity';
import { geodesicCirclePathLonLat } from '../../utils/exerciseConvergenceCirclePath';
import { SCENARIO_COMMS_SCRIPT, MOC_SHIP_LON_LAT } from '../../data/scenarioCommsData';
import { getScenarioCommsMessageEndpoints } from '../../utils/scenarioCommsPositions';
import { hasExerciseVesselFixAtPlayback } from '../../utils/exerciseVesselVisibility';
import { useOcean } from '../../contexts/OceanDataContext';

const SCENARIO_COMMS_LINK_STYLES = {
  'moc-command': { main: '#817c9e', soft: 'rgba(129, 124, 158, 0.28)' },
  'moc-subsea': { main: '#06b6d4', soft: 'rgba(6, 182, 212, 0.22)' },
  'moc-threat': { main: '#f97316', soft: 'rgba(249, 115, 22, 0.24)' },
  'moc-recovery': { main: '#22c55e', soft: 'rgba(34, 197, 94, 0.22)' }
};

/**
 * Pixel layout for scenario comms link (SVG line + endpoints) from [lon,lat] pair.
 * Recompute whenever the map transform changes (pan/zoom); do not cache once.
 */
function computeScenarioCommsLinkLayoutPx(map, fromLL, toLL, strokeMain, strokeSoft) {
  if (!map || !fromLL || !toLL) return null;
  let p1;
  let p2;
  try {
    p1 = map.project(fromLL);
    p2 = map.project(toLL);
  } catch (_) {
    return null;
  }
  if (!p1 || !p2 || typeof p1.x !== 'number' || typeof p2.x !== 'number') return null;
  return {
    x1: p1.x,
    y1: p1.y,
    x2: p2.x,
    y2: p2.y,
    strokeMain,
    strokeSoft
  };
}

// A single, reusable particle layer for wind, ocean currents, etc. - OPTIMIZED (reserved for future use)
/* eslint-disable-next-line no-unused-vars */
class ParticleLayer extends CompositeLayer {
  static layerName = 'ParticleLayer';
  static defaultProps = {
    data: [],
    bbox: { minLng: -180, maxLng: 180, minLat: -90, maxLat: 90 },
    particleCount: 5000,
    maxAge: 40,
    opacity: 0.8,
    time: 0,
    particleSpeedFactor: 1.0,
    vectorScale: 1.0,
    arrowColor: 'blue',
    pauseAnimation: false,
    particleType: 'currents',

    getPosition: d => [d.lon, d.lat],
    getSpeed: d => d.nspeed || d.speed || 0,
    getDirection: d => d.direction || 0,
    getColor: (value, alpha) => [255, 255, 255, alpha]
  };

  getVector(lon, lat, data) {
    if (this._lastDataRef !== data) {
      this._spatialIndex = null;
      this._lastDataRef = data;
    }

    if (!this._spatialIndex) {
      this._spatialIndex = new Map();
      const gridSize = 0.05;

      for (const point of data) {
        if (point.lon == null || point.lat == null) continue;
        const gridX = Math.floor(point.lon / gridSize);
        const gridY = Math.floor(point.lat / gridSize);
        const key = `${gridX},${gridY}`;

        if (!this._spatialIndex.has(key)) {
          this._spatialIndex.set(key, []);
        }
        this._spatialIndex.get(key).push(point);
      }
    }

    const gridSize = 0.05;
    const gridX = Math.floor(lon / gridSize);
    const gridY = Math.floor(lat / gridSize);
    let nearestPoint = null;
    let minDistanceSq = Infinity;

    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${gridX + dx},${gridY + dy}`;
        const points = this._spatialIndex.get(key);
        if (!points) continue;

        for (const point of points) {
          const dLon = lon - point.lon;
          const dLat = lat - point.lat;
          const distanceSq = dLon * dLon + dLat * dLat;
          if (distanceSq < minDistanceSq) {
            minDistanceSq = distanceSq;
            nearestPoint = point;
          }
        }
      }
    }

    if (!nearestPoint) {
      return { u: 0, v: 0 };
    }
    const speed = nearestPoint.nspeed || nearestPoint.speed || 0;
    const direction = nearestPoint.direction || 0;
    const angleRad = (90 - direction) * (Math.PI / 180);
    return { u: speed * Math.cos(angleRad), v: speed * Math.sin(angleRad) };
  }

  initializeState() {
    this.setState({
      particles: this.generateParticles(),
      lastUpdateTime: 0,
      frameCounter: 0
    });
  }

  updateState({ props, oldProps, changeFlags }) {
    if (changeFlags.dataChanged) {
      this._spatialIndex = null;
    }

    if (props.pauseAnimation && this.state.particles.length > 0) {
      this.setState({ frameCounter: this.state.frameCounter + 1 });
      props.onDebug({
        particleCount: this.state.particles.length,
        frame: this.state.frameCounter + 1,
        dataChanged: changeFlags.dataChanged,
        layer: props.id.includes('wind') ? 'wind' : 'currents'
      });
      return;
    }

    const { frameCounter } = this.state;
    const newFrameCounter = frameCounter + 1;
    const needsInit = this.state.particles.length === 0 && props.data && props.data.length > 0;
    const shouldUpdate = newFrameCounter % 2 === 0 || changeFlags.propsChanged || changeFlags.dataChanged || needsInit;

    if (shouldUpdate) {
      let nextParticles = this.state.particles;
      if (needsInit || changeFlags.dataChanged) {
        nextParticles = this.generateParticles();
      } else {
        nextParticles = this.updateParticles();
      }

      this.setState({
        particles: nextParticles,
        lastUpdateTime: props.time,
        frameCounter: newFrameCounter
      });

      props.onDebug({
        particleCount: nextParticles.length,
        frame: newFrameCounter,
        dataChanged: changeFlags.dataChanged,
        layer: props.id.includes('wind') ? 'wind' : 'currents'
      });
    } else {
      this.setState({ frameCounter: newFrameCounter });
      props.onDebug({
        particleCount: this.state.particles.length,
        frame: newFrameCounter,
        dataChanged: changeFlags.dataChanged,
        layer: props.id.includes('wind') ? 'wind' : 'currents'
      });
    }
  }

  generateParticles() {
    const { particleCount, data, getPosition } = this.props;
    if (!data || data.length === 0) return [];

    this._spatialIndex = new Map();
    this._lastDataRef = data;
    const gridSize = 0.05;
    for (const point of data) {
      if (point.lon == null || point.lat == null) continue;
      const gridX = Math.floor(point.lon / gridSize);
      const gridY = Math.floor(point.lat / gridSize);
      const key = `${gridX},${gridY}`;
      if (!this._spatialIndex.has(key)) {
        this._spatialIndex.set(key, []);
      }
      this._spatialIndex.get(key).push(point);
    }

    const particles = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push(this.createParticle(i, data, getPosition));
    }
    return particles;
  }

  createParticle(id, data, getPosition) {
    const sourcePoint = data[Math.floor(Math.random() * data.length)];
    const position = [...getPosition(sourcePoint)];
    const speed = sourcePoint.nspeed || sourcePoint.speed || 0;
    const direction = sourcePoint.direction || 0;

    return {
      id,
      position,
      path: [position],
      age: Math.random() * this.props.maxAge,
      maxAge: this.props.maxAge + Math.random() * 20,
      speed,
      direction,
      cachedVector: {
        u: speed * Math.cos((90 - direction) * (Math.PI / 180)),
        v: speed * Math.sin((90 - direction) * (Math.PI / 180))
      }
    };
  }

  updateParticles() {
    const { bbox, particleSpeedFactor, data, getPosition } = this.props;
    const { particles } = this.state;
    if (!data || data.length === 0) return particles;

    return particles.map(p => {
      let vec;
      if (p.cachedVector && p.age < 2) {
        vec = p.cachedVector;
      } else {
        vec = this.getVector(p.position[0], p.position[1], data);
      }

      const dt = 0.05 * particleSpeedFactor;
      const nextPos = [
        p.position[0] + vec.u * dt,
        p.position[1] + vec.v * dt
      ];

      let newAge = p.age + 1;
      let newPath = [...p.path, nextPos];
      if (newPath.length > 5) newPath.shift();

      const speed = Math.sqrt(vec.u ** 2 + vec.v ** 2);
      const isOutOfBounds = nextPos[0] < bbox.minLng || nextPos[0] > bbox.maxLng ||
        nextPos[1] < bbox.minLat || nextPos[1] > bbox.maxLat;

      if (newAge > p.maxAge || isOutOfBounds || (p.age > 5 && speed < 0.001)) {
        return this.createParticle(p.id, data, getPosition);
      }

      return {
        ...p,
        position: nextPos,
        path: newPath,
        age: newAge,
        speed: speed,
        maxAge: p.maxAge
      };
    });
  }

  renderLayers() {
    const { particles, frameCounter } = this.state;
    const { opacity, getColor } = this.props;

    if (!particles || particles.length === 0) return [];

    const updateKey = Math.floor(frameCounter / 4);
    const validPaths = particles.filter(p => p.path.length > 1);

    return [
      new ScatterplotLayer({
        id: `${this.props.id}-heads`,
        data: particles,
        getPosition: d => d.position,
        getRadius: d => {
          const life = d.age / d.maxAge;
          if (life < 0.1) return life * 30000;
          if (life > 0.9) return (1 - life) * 30000;
          return 3000;
        },
        radiusMinPixels: 1.5,
        radiusMaxPixels: 4,
        getFillColor: d => getColor(d.speed * 1000, 255 * opacity),
        pickable: false,
        updateTriggers: {
          getPosition: [frameCounter],
          getRadius: [updateKey],
          getFillColor: [updateKey]
        }
      }),
      new LineLayer({
        id: `${this.props.id}-trails`,
        data: validPaths,
        getSourcePosition: d => d.path[0],
        getTargetPosition: d => d.position,
        getColor: d => getColor(d.speed * 1000, 150 * opacity),
        getWidth: 2,
        widthMinPixels: 1,
        widthMaxPixels: 2,
        capRounded: true,
        opacity: 0.6,
        updateTriggers: {
          getSourcePosition: [frameCounter],
          getTargetPosition: [frameCounter],
          getColor: [updateKey]
        }
      })
    ];
  }
}

const ARROW_ICON_MAPPING = {
  arrow: { x: 0, y: 0, width: 32, height: 32, anchorY: 32, mask: true }
};

// Constants for icons and styles
const RELATIVE_GRID_STYLE = {
  version: 8,
  name: 'Relative Grid View Mode',
  sources: {},
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: {
        'background-color': '#0f172a' // slate-900 background for a clean tactical view
      }
    }
  ]
};

const ARROW_ICON_ATLAS = (() => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <polygon points="16,2 28,28 16,22 4,28" fill="white" stroke="none"/>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
})();

// Generate visually distinct colors for vessel tracks using a golden-ratio hue sequence (reserved for future use)
/* eslint-disable-next-line no-unused-vars */
const _getTrackColor = (trackId) => {
  const goldenRatioConjugate = 0.61803398875;
  let h = (trackId * goldenRatioConjugate) % 1; // 0-1
  const s = 0.65;
  const l = 0.55;

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

// Step 4: cluster circles (number circles) only until zoom 15; from zoom 16 onward show individual vessel icons
const VESSEL_CLUSTER_MAX_ZOOM = 15;

/**
 * True if this deck.gl layer shows a vessel marker (dots, icons, demo PNGs use suffixes like -demo-threat).
 * Must stay in sync with MarkerClusterLayer / buildDemoVesselIconLayers id patterns.
 */
function isVesselPickLayerId(layerId) {
  if (!layerId || typeof layerId !== 'string') return false;
  return (
    layerId.startsWith('vessel-tracking-clusters') ||
    layerId.startsWith('marker-clusters') ||
    layerId.startsWith('vessel-dots-layer') ||
    layerId.startsWith('vessel-tracking-unclustered-dots') ||
    layerId.startsWith('exercise-vessel-heads') ||
    layerId.startsWith('exercise-op-alpha-proximity-threat-core') ||
    layerId === 'exercise-vessel-tracks' ||
    layerId.startsWith('vessel-layer-')
  );
}

// Status colors for vessel markers: red = threat, green = known, yellow = unknown/suspicious, grey = inactive
const VESSEL_STATUS_RGB = {
  THREAT: [239, 68, 68],      // red-500
  KNOWN: [34, 197, 94],       // green-500 (identified, non-threat)
  UNKNOWN: [234, 179, 8],     // yellow-500
  INACTIVE: [107, 114, 128]   // gray-500
};

/** Autonomous Underwater Vehicle map color (non-threat). */
const AUV_VESSEL_DOT_RGB = [227, 142, 37]; // #e38e25

/** Defensive surface USV map color (non-threat). */
const DEFENSIVE_SURFACE_VESSEL_DOT_RGB = [129, 123, 230]; // #817be6

/** Scenario MOC ship marker (fill + darker stroke). */
const MOC_MARKER_FILL_RGB = [129, 124, 158]; // #817c9e
const MOC_MARKER_STROKE_RGB = [82, 76, 99];

const normalizeVesselCategoryKey = (obj) =>
  String(
    obj?.common_name ??
      obj?.message_description_label ??
      obj?.message_description_name ??
      obj?.object_classification ??
      ''
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const isThreatOrUnknownUsvKey = (key) =>
  Boolean(key) && (key.includes('threat') || (key.includes('unknown') && key.includes('usv')));

/**
 * Defensive surface USV by normalized key only (no AUV call — avoids classifying
 * defensive_surface_*auv* strings as AUV orange).
 */
const isDefensiveSurfaceVesselKey = (key) => {
  if (!key || isThreatOrUnknownUsvKey(key)) return false;
  return (
    key.includes('defensive_surface') ||
    key.includes('defense_usv') ||
    key.includes('defensive_usv') ||
    key.includes('surface_defense') ||
    key.includes('blue_force_usv')
  );
};

const isDefensiveSurfaceVesselCategory = (obj) =>
  isDefensiveSurfaceVesselKey(normalizeVesselCategoryKey(obj));

/** True for AUV / UUV / "Autonomous Underwater Vehicle" (defensive keys excluded first). */
const isAutonomousUnderwaterVehicleCategory = (obj) => {
  const key = normalizeVesselCategoryKey(obj);
  if (!key) return false;
  if (isDefensiveSurfaceVesselKey(key)) return false;
  if (/(^|_)auv(_|$)/.test(key)) return true;
  if (key.includes('autonomous_underwater')) return true;
  if (key.includes('underwater_vehicle')) return true;
  if (key.includes('uuv')) return true;
  return false;
};

/** Exercise deck.gl row: AUV — never treat Defense USV labels as AUV. */
const isExerciseAuvHeadOrPath = (d) => {
  const lab = String(d?.label || '');
  if (/defense\s*usv/i.test(lab)) return false;
  if (/defensive\s*surface/i.test(lab) && /\busv\b/i.test(lab)) return false;
  if (/\bauv\b/i.test(lab)) return true;
  return isAutonomousUnderwaterVehicleCategory(d?.tooltipRow || d);
};

/** Exercise deck.gl row: Defense / defensive-surface USV label or category on tooltip row. */
const isExerciseDefenseHeadOrPath = (d) => {
  const lab = String(d?.label || '');
  if (/defense\s*usv/i.test(lab)) return true;
  if (/defensive\s*surface/i.test(lab) && /\busv\b/i.test(lab)) return true;
  return isDefensiveSurfaceVesselCategory(d?.tooltipRow || d);
};

/**
 * Dot / cluster / arrow RGB: threat → red; defensive USV → #817be6 (before AUV); AUV → #e38e25; else status.
 */
const getVesselMapDotRgb = (d) => {
  if (!d) return VESSEL_STATUS_RGB.UNKNOWN;
  const isThreat = d.threat_detected === 'Yes' || d.threat_detected === true;
  if (isThreat) return VESSEL_STATUS_RGB.THREAT;
  if (isDefensiveSurfaceVesselCategory(d)) return DEFENSIVE_SURFACE_VESSEL_DOT_RGB;
  if (isAutonomousUnderwaterVehicleCategory(d)) return AUV_VESSEL_DOT_RGB;
  return getVesselStatusColor(d);
};

/** Returns [r,g,b] for vessel marker by status. Used for dots, pulse ring, hover ring, and velocity arrows. */
const getVesselStatusColor = (d) => {
  if (!d) return VESSEL_STATUS_RGB.UNKNOWN;
  const isThreat = d.threat_detected === 'Yes' || d.threat_detected === true;
  if (isThreat) return VESSEL_STATUS_RGB.THREAT;
  const speed = d.speed_knots ?? d.speed ?? 0;
  const speedNum = Number(speed);
  const isInactive = !Number.isNaN(speedNum) && speedNum < 0.5;
  if (isInactive) return VESSEL_STATUS_RGB.INACTIVE;
  const id = d.unique_identifier;
  const hasId = id != null && String(id).trim() !== '';
  return hasId ? VESSEL_STATUS_RGB.KNOWN : VESSEL_STATUS_RGB.UNKNOWN;
};

/** Step 6: Status label for tooltip/popup – same logic as getVesselStatusColor. */
const getVesselStatusLabel = (d) => {
  if (!d) return 'Unknown';
  const isThreat = d.threat_detected === 'Yes' || d.threat_detected === true;
  if (isThreat) return 'Threat';
  const speed = d.speed_knots ?? d.speed ?? 0;
  const speedNum = Number(speed);
  const isInactive = !Number.isNaN(speedNum) && speedNum < 0.5;
  if (isInactive) return 'Inactive';
  const id = d.unique_identifier;
  const hasId = id != null && String(id).trim() !== '';
  return hasId ? 'Known' : 'Unknown';
};

/** Step 6: Tailwind text color class for status – matches map dot colors (defensive before AUV). */
const getVesselStatusTextClass = (d) => {
  const isThreat = d && (d.threat_detected === 'Yes' || d.threat_detected === true);
  if (!isThreat && isDefensiveSurfaceVesselCategory(d)) {
    return 'text-[#817be6] dark:text-[#a29bf0] font-medium';
  }
  if (!isThreat && isAutonomousUnderwaterVehicleCategory(d)) {
    return 'text-[#e38e25] dark:text-[#f0a84d] font-medium';
  }
  const label = getVesselStatusLabel(d);
  switch (label) {
    case 'Threat': return 'text-red-500 dark:text-red-400 font-semibold';
    case 'Known': return 'text-green-500 dark:text-green-400';
    case 'Unknown': return 'text-yellow-500 dark:text-yellow-400';
    case 'Inactive': return 'text-slate-500 dark:text-slate-400';
    default: return 'text-slate-500 dark:text-slate-400';
  }
};

// Legacy: threat-only for tooltip text (red vs green) (reserved for future use)
/* eslint-disable-next-line no-unused-vars */
const _getThreatColor = (d) => {
  const isThreat = d && (d.threat_detected === 'Yes' || d.threat_detected === true);
  return isThreat ? [239, 68, 68] : [34, 197, 94]; // red : green
};

// Raw category string (same source for display + icon so tooltip and map icon always match)
const getVesselCategoryRaw = (obj) => {
  if (!obj) return '';
  return String(obj.common_name || obj.message_description_label || obj.message_description_name || obj.object_classification || '').trim();
};
// Vessel category from common_name (e.g. "fishing_boat" / "Autonomous Underwater Vehicle" → title case per word)
const getVesselCategoryName = (obj) => {
  const s = getVesselCategoryRaw(obj);
  if (!s) return 'Vessel';
  return s
    .replace(/_/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper to determine if two clicked points represent the same vessel sample (reserved for future use)
/* eslint-disable-next-line no-unused-vars */
const _isSameTrackPoint = (a, b) => {
  if (!a || !b) return false;
  if (a === b) return true;

  // Prefer unique_identifier + timestamp when available (Awarion data)
  if (a.unique_identifier && b.unique_identifier) {
    if (a.unique_identifier === b.unique_identifier && a.timestamp && b.timestamp) {
      return a.timestamp === b.timestamp;
    }
  }

  // Fallback: compare by lat/lon/time
  if (a.lat != null && b.lat != null && a.lon != null && b.lon != null && a.time && b.time) {
    return (
      Math.abs(a.lat - b.lat) < 1e-6 &&
      Math.abs(a.lon - b.lon) < 1e-6 &&
      a.time === b.time
    );
  }

  return false;
};

const generateArrowVectorData = (data, options = {}) => {
  if (!data || data.length === 0) return [];

  const {
    sampleRate = 5,
    arrowLength = 0.03,
    speedScale = 1.0,
    type = 'currents'
  } = options;

  const sampledData = data.filter((_, i) => i % sampleRate === 0);

  return sampledData.map(d => {
    const lon = d.lon;
    const lat = d.lat;
    const speed = d.nspeed || d.speed || 0;
    const direction = d.direction || 0;
    const angleRad = (90 - direction) * (Math.PI / 180);
    const length = arrowLength * (0.5 + speed * speedScale * 0.5);
    const endLon = lon + Math.cos(angleRad) * length;
    const endLat = lat + Math.sin(angleRad) * length;

    let color;
    if (type === 'wind') {
      const intensity = Math.min(1, speed / 2);
      color = [
        Math.round(255),
        Math.round(50 + intensity * 150),
        Math.round(150 - intensity * 100),
        255
      ];
    } else {
      const intensity = Math.min(1, speed / 1.5);
      color = [
        Math.round(0 + intensity * 100),
        Math.round(220 + intensity * 35),
        Math.round(255),
        255
      ];
    }

    return {
      sourcePosition: [lon, lat],
      targetPosition: [endLon, endLat],
      color,
      speed,
      direction
    };
  });
};

const generateHeatmapData = (data, parameter, options = {}) => {
  if (!data || data.length === 0) return [];

  const { depthFilter = null, normalize = true } = options;
  let filteredData = data;

  if (depthFilter !== null && depthFilter !== undefined) {
    filteredData = data.filter(d => Math.abs(d.depth - depthFilter) < 0.3048);
  }

  const validData = filteredData.filter(d => d[parameter] != null && !isNaN(d[parameter]) && d.lat != null && d.lon != null);
  if (validData.length === 0) return [];

  let minVal = Infinity;
  let maxVal = -Infinity;
  if (normalize) {
    validData.forEach(d => {
      if (d[parameter] < minVal) minVal = d[parameter];
      if (d[parameter] > maxVal) maxVal = d[parameter];
    });
  }

  const range = maxVal - minVal;
  if (normalize && range === 0) {
    return validData.map(d => [d.lat, d.lon, 0.5]);
  }

  return validData.map(d => {
    const weight = normalize ? (d[parameter] - minVal) / range : d[parameter];
    return [d.lat, d.lon, weight];
  });
};

const TEMPERATURE_COLOR_RANGE = [
  [2, 59, 150], [36, 178, 208], [149, 235, 151], [254, 218, 107], [252, 114, 61], [239, 48, 48]
];
const SALINITY_COLOR_RANGE = [
  [237, 248, 251], [179, 226, 225], [102, 194, 164], [44, 162, 95], [0, 109, 44]
];
const SSH_COLOR_RANGE = [
  [43, 131, 186], [171, 221, 164], [255, 255, 191], [253, 174, 97], [215, 25, 28]
];
const PRESSURE_COLOR_RANGE = [
  [255, 247, 236], [254, 227, 184], [253, 190, 133], [253, 141, 60], [217, 71, 1]
];

/* eslint-disable-next-line no-unused-vars */
const _layerDisplayNames = {
  oceanCurrents: 'Ocean Currents',
  temperature: 'Temperature',
  ssh: 'Surface Elevation',
  salinity: 'Salinity',
  pressure: 'Pressure',
  windParticles: 'Wind Particles',
};


// Improved Wind Particle Layer with better visibility


/**
 * Generate arrow vector data for simple visualization
 * Each arrow shows direction and speed of flow at a data point
 * @param {Array} data - Array of data points with lon, lat, speed, direction
 * @param {Object} options - Configuration options
 * @returns {Array} Array of arrow objects with start/end positions and color
 */

/**
 * Generate velocity vector arrow data for vessels/threats (bearing + speed) for trajectory visualization.
 * Uses heading_deg (0 = North, 90 = East) and speed_knots; colors by threat_detected.
 * @param {Array} data - Vessel points with lon, lat, heading_deg/direction, speed_knots/speed
 * @param {Object} options - { arrowLengthScale }
 * @returns {Array} Arrow objects with sourcePosition, targetPosition, color, speed_knots, heading_deg
 */
function generateVesselVelocityArrowData(data, options = {}) {
  if (!data || data.length === 0) return [];
  const arrowLengthScale = options.arrowLengthScale ?? 0.035;
  const zoom = options.zoom ?? 9;
  const refZoom = 9;
  const zoomScale = Math.max(0.15, Math.min(64, 1 / Math.pow(2, zoom - refZoom)));
  return data
    .filter(d => {
      const h = resolveVesselMaritimeHeadingDeg(d);
      const s = d.speed_knots ?? d.speed;
      return d.lat != null && d.lon != null && !isNaN(d.lat) && !isNaN(d.lon) && h != null && !isNaN(Number(h)) && s != null && !isNaN(Number(s));
    })
    .map(d => {
      const lon = Number(d.lon);
      const lat = Number(d.lat);
      const headingDeg = Number(resolveVesselMaritimeHeadingDeg(d) ?? 0);
      const speedKnots = Number(d.speed_knots ?? d.speed ?? 0);
      const angleRad = (90 - headingDeg) * (Math.PI / 180);
      const speedFactor = 0.15 + speedKnots / 8;
      const length = arrowLengthScale * speedFactor * zoomScale;
      const endLon = lon + Math.cos(angleRad) * length;
      const endLat = lat + Math.sin(angleRad) * length;
      const statusRgb = getVesselMapDotRgb(d);
      const color = [...statusRgb, 220];
      const dLon = endLon - lon;
      const dLat = endLat - lat;
      const angleFromEastRad = Math.atan2(dLat, dLon);
      const angleDeg = ((angleFromEastRad * 180) / Math.PI + 360) % 360;
      return {
        sourcePosition: [lon, lat],
        targetPosition: [endLon, endLat],
        color,
        speed_knots: speedKnots,
        heading_deg: headingDeg,
        angleDeg
      };
    });
}

const MapContainer = ({
  stationData = [],
  timeSeriesData = [],
  rawData = [],
  vesselsById = null,
  selectedVesselId = null,
  threatLevelFilter = 'all',
  vesselTypeFilter = [],
  totalFrames = 0,
  currentFrame = 0,
  selectedDepth = 0,
  selectedArea = '',
  onDepthChange,
  onStationSelect,
  onEnvironmentUpdate,
  currentDate = '',
  currentTime = '',
  mapboxToken,
  isOutputCollapsed = false,
  outputPanelWidth,
  isOutputPanelResizing = false,
  initialViewState = {
    longitude: -71.3128, // Rhode Island (Newport area)
    latitude: 41.4901,
    zoom: 12,
    pitch: 0,
    bearing: 0
  },
  // Layer props
  mapLayerVisibility = {
    oceanCurrents: false,
    temperature: false,
    salinity: false,
    ssh: false,
    pressure: false,
    stations: false,
    windSpeed: false,
    windDirection: false,
    windVelocity: false,
  },
  // Layer animation controls (particle/vector layers default to paused)
  layerAnimationEnabled = {
    oceanCurrents: false,
    windVelocity: false,
  },
  currentsVectorScale = 0.009,
  currentsColorBy = 'speed',
  heatmapScale = 1,
  // Available depths for POV slider
  availableDepths = [],
  // Data bounds for map centering
  dataBounds = null,
  viewMode = 'Ocean Analysis',
  /** 'dots' | 'icons' — demo vessel PNG markers; only applied when Exercise scenario layer is on (see ControlPanel) */
  vesselMarkerDisplay = 'dots',
  // Playback controls from parent
  isPlaying = false,
  playbackSpeed = 0.1,
  loopMode = 'Once',
  onPausePlayback,
  // Vessel playback time (when in Vessel Tracking; controlled from ControlPanel)
  playbackTime: playbackTimeProp,
  setPlaybackTime: setPlaybackTimeProp,
  /** Revealed scenario comms index from story playback (Exercise scenario on) — map bursts at scripted anchors */
  scenarioCommsVisibleStep = 0,
  /** Bumps when user resets playback — clears map comms burst + sidebar log */
  playbackResetSerial = 0
}) => {
  const { setScenarioCommsBurst, notifyPlaybackReset } = useOcean();
  const mapRef = useRef();
  const mapContainerRef = useRef();
  const currentMapStyleRef = useRef('arcgis-ocean'); // Track current map style

  const [mapContainerReady, setMapContainerReady] = useState(false);
  const [, setMapInstanceReady] = useState(false);
  const [viewState, setViewState] = useState(initialViewState);
  const [hoveredStation, setHoveredStation] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [trackInfo, setTrackInfo] = useState(null); // { x, y, object }
  const [hoveredVesselPoint, setHoveredVesselPoint] = useState(null);
  const [hoveredVesselTooltip, setHoveredVesselTooltip] = useState(null); // { object, x, y } — shown on vessel icon click only
  const [vesselPulseTick, setVesselPulseTick] = useState(0);

  // Vessel playback: use props when provided (ControlPanel), else internal state
  const [internalPlaybackTime, setInternalPlaybackTime] = useState(null);
  const playbackTime = playbackTimeProp !== undefined ? playbackTimeProp : internalPlaybackTime;
  const setPlaybackTime = setPlaybackTimeProp !== undefined ? setPlaybackTimeProp : setInternalPlaybackTime;

  /** Guards vessel playback interval — avoids advancing time if pause state lags effect cleanup. */
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  const lastFrameTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const sortedVesselDataRef = useRef([]);
  const [clusteredVesselData, setClusteredVesselData] = useState([]);
  const exerciseScenarioFramedRef = useRef(false);

  /** Ephemeral scenario comms burst on map (stream text, then hide). */
  const [commsBurst, setCommsBurst] = useState(null);
  const prevScenarioCommsStepRef = useRef(0);
  const wasExerciseVesselVisibleRef = useRef(false);
  const commsBurstGenRef = useRef(0);
  const commsStreamIntervalRef = useRef(null);
  const commsHoldTimeoutRef = useRef(null);
  const commsHideTimeoutRef = useRef(null);
  const commsMoveRafRef = useRef(null);
  /** Bumps when the map moves while a comms burst is visible so overlay re-projects every frame. */
  const [, setCommsMapMoveSerial] = useState(0);

  const invalidateCommsBurstTimers = useCallback(() => {
    commsBurstGenRef.current += 1;
    if (commsStreamIntervalRef.current) {
      clearInterval(commsStreamIntervalRef.current);
      commsStreamIntervalRef.current = null;
    }
    if (commsHoldTimeoutRef.current) {
      clearTimeout(commsHoldTimeoutRef.current);
      commsHoldTimeoutRef.current = null;
    }
    if (commsHideTimeoutRef.current) {
      clearTimeout(commsHideTimeoutRef.current);
      commsHideTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    commsBurstGenRef.current += 1;
    if (commsStreamIntervalRef.current) clearInterval(commsStreamIntervalRef.current);
    if (commsHoldTimeoutRef.current) clearTimeout(commsHoldTimeoutRef.current);
    if (commsHideTimeoutRef.current) clearTimeout(commsHideTimeoutRef.current);
  }, []);

  useEffect(() => {
    invalidateCommsBurstTimers();
    setCommsBurst(null);
    prevScenarioCommsStepRef.current = 0;
    wasExerciseVesselVisibleRef.current = false;
  }, [playbackResetSerial, invalidateCommsBurstTimers]);

  // Ocean-focused controls with default settings - ArcGIS Ocean as default
  const [userInteracting, setUserInteracting] = useState(false);
  const [spinEnabled, setSpinEnabled] = useState(false);
  const [mapStyle, setMapStyle] = useState('arcgis-ocean');
  const [showMapControls, setShowMapControls] = useState(false);

  // Grid layer controls
  const [showGrid, setShowGrid] = useState(true);
  const [gridOpacity, setGridOpacity] = useState(1.0);
  const [gridSpacing, setGridSpacing] = useState(1);
  const [gridColor] = useState([100, 149, 237, 128]); // Cornflower blue

  // Data availability tooltip state
  const [coordinateHover, setCoordinateHover] = useState(null);
  const coordinateHoverLastRef = useRef(0);
  const coordinateHoverSigRef = useRef('');

  // Frame map on the scripted exercise area when the overlay is turned on
  useEffect(() => {
    if (!mapLayerVisibility.exerciseScenario) {
      exerciseScenarioFramedRef.current = false;
      return;
    }
    if (!mapContainerReady || !mapRef.current || exerciseScenarioFramedRef.current) return;
    const map = mapRef.current;
    map.fitBounds(
      [
        [EXERCISE_SCENARIO_BOUNDS.west, EXERCISE_SCENARIO_BOUNDS.south],
        [EXERCISE_SCENARIO_BOUNDS.east, EXERCISE_SCENARIO_BOUNDS.north]
      ],
      { padding: { top: 64, bottom: 64, left: 64, right: 64 }, duration: 1400 }
    );
    exerciseScenarioFramedRef.current = true;
  }, [mapLayerVisibility.exerciseScenario, mapContainerReady]);

  // Recenter map when dataBounds become available
  useEffect(() => {
    if (mapLayerVisibility.exerciseScenario) return;
    if (dataBounds && dataBounds.centerLat && dataBounds.centerLon && mapRef.current) {
      // Choose a zoom level based on spatial extent for a closer view of clusters
      let targetZoom = 12;
      if (typeof dataBounds.minLon === 'number' && typeof dataBounds.maxLon === 'number' &&
          typeof dataBounds.minLat === 'number' && typeof dataBounds.maxLat === 'number') {
        const lonSpan = Math.abs(dataBounds.maxLon - dataBounds.minLon);
        const latSpan = Math.abs(dataBounds.maxLat - dataBounds.minLat);
        const span = Math.max(lonSpan, latSpan);

        if (span < 0.05) targetZoom = 13;      // very tight cluster
        else if (span < 0.1) targetZoom = 12;  // small area
        else if (span < 0.3) targetZoom = 11;  // regional
        else if (span < 1.0) targetZoom = 10;  // larger region
        else targetZoom = 8;                   // wide coverage
      }

      // Step 2 (Vincent): start more zoomed in so users don't have to zoom in as much; leave existing logic unchanged
      const DEFAULT_ZOOM_BOOST = 2;
      const MAX_ZOOM = 15;
      targetZoom = Math.min(MAX_ZOOM, targetZoom + DEFAULT_ZOOM_BOOST);

      mapRef.current.flyTo({
        center: [dataBounds.centerLon, dataBounds.centerLat],
        zoom: targetZoom,
        duration: 2000
      });
      setViewState(prev => ({
        ...prev,
        longitude: dataBounds.centerLon,
        latitude: dataBounds.centerLat,
        zoom: targetZoom
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dataBounds intentionally not in deps to avoid recentering on every data change
  }, [
    mapLayerVisibility.exerciseScenario,
    mapContainerReady,
    dataBounds?.centerLat,
    dataBounds?.centerLon,
    dataBounds?.minLon,
    dataBounds?.maxLon,
    dataBounds?.minLat,
    dataBounds?.maxLat
  ]);

  // Local pulse for vessel dots: use setInterval instead of rAF to avoid "Maximum update depth exceeded".
  // Updating state from rAF every frame can trigger the error; ~5 updates/sec is enough for a smooth pulse.
  useEffect(() => {
    if (viewMode !== 'Vessel Tracking' || !mapContainerReady || !isPlaying) return;
    const intervalMs = 200;
    const id = setInterval(() => {
      setVesselPulseTick((t) => (t + 1) % 1000000);
    }, intervalMs);
    return () => clearInterval(id);
  }, [viewMode, mapContainerReady, isPlaying]);

  // Vessel pulse intensity from local tick (smooth, independent of currentFrame) - reserved for future use
  /* eslint-disable-next-line no-unused-vars */
  const _vesselPulseIntensity = 1 + Math.sin(vesselPulseTick * 0.08) * 0.35;

  // Memoize animation calculations to reduce per-frame computation
  const animationValues = useMemo(() => {
    const animationTime = Math.floor(currentFrame / 3) * 0.1; // Update every 3 frames
    const pulse = 1 + Math.sin(animationTime * 2) * 0.4; // Size pulse
    const radius = 1 + Math.sin(animationTime * 1.5) * 0.3; // Radius pulse
    // Color brightness pulse - oscillates between 0.7 and 1.0 for dynamic effect
    const colorPulse = 0.85 + Math.sin(animationTime * 3) * 0.15;

    return {
      pulseIntensity: pulse,
      radiusAnimation: radius,
      colorPulse: colorPulse
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentFrame/3 is the effective dependency
  }, [currentFrame]);

  // Heatmap data generation for all relevant layers
  const temperatureHeatmapData = useMemo(() => {
    if (!mapLayerVisibility.temperature || !rawData || rawData.length === 0) return [];
    return generateHeatmapData(rawData, 'temp', { depthFilter: selectedDepth });
  }, [rawData, mapLayerVisibility.temperature, selectedDepth]);

  const salinityHeatmapData = useMemo(() => {
    if (!mapLayerVisibility.salinity || !rawData || rawData.length === 0) return [];
    return generateHeatmapData(rawData, 'salinity', { depthFilter: selectedDepth });
  }, [rawData, mapLayerVisibility.salinity, selectedDepth]);

  const sshHexagonData = useMemo(() => {
    if (!mapLayerVisibility.ssh || !rawData || rawData.length === 0) return [];
    return rawData.filter(d => d.ssh != null && !isNaN(d.ssh) && d.lat != null && d.lon != null);
  }, [rawData, mapLayerVisibility.ssh]);

  const pressureHeatmapData = useMemo(() => {
    if (!mapLayerVisibility.pressure || !rawData || rawData.length === 0) return [];
    return generateHeatmapData(rawData, 'pressure_dbars', { depthFilter: selectedDepth });
  }, [rawData, mapLayerVisibility.pressure, selectedDepth]);

  const windSourceData = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];
    return rawData.filter(d =>
      d.nspeed != null &&
      d.ndirection != null &&
      d.lat != null &&
      d.lon != null &&
      isLikelyOnWater(d.lat, d.lon)
    );
  }, [rawData]);

  // Threat hull / pulses only use rows at or before current story time (unified with playback clock).
  const dataForThreatLayers = useMemo(() => {
    if (playbackTime == null || !rawData || rawData.length === 0) return rawData;
    return rawData.filter(row => {
      const t = getRowTimeMs(row);
      if (Number.isNaN(t)) return false;
      return t <= playbackTime;
    });
  }, [rawData, playbackTime]);

  const exerciseVesselDeckData = useMemo(() => {
    if (!mapLayerVisibility.exerciseScenario) return { paths: [], heads: [] };
    return buildExerciseVesselPathsFromRawData(rawData, playbackTime ?? null);
  }, [mapLayerVisibility.exerciseScenario, rawData, playbackTime]);

  /** Split exercise heads: unknown-surface contact uses red pulse when near Op Alpha instead of role-colored dot. */
  const { exerciseOpAlphaPulseRows, exerciseHeadsForDots } = useMemo(() => {
    const heads = exerciseVesselDeckData.heads;
    if (!mapLayerVisibility.exerciseScenario || heads.length === 0) {
      return { exerciseOpAlphaPulseRows: [], exerciseHeadsForDots: heads };
    }
    const vid = EXERCISE_OP_ALPHA_PROXIMITY_THREAT_VESSEL_ID;
    const buf = EXERCISE_OP_ALPHA_PROXIMITY_BUFFER_M;
    const exerciseOpAlphaPulseRows = [];
    const exerciseHeadsForDots = [];
    for (const h of heads) {
      if (String(h.id) === vid) {
        const [lon, lat] = h.position;
        if (isVesselNearOpAlphaThreatZone(lon, lat, buf)) {
          exerciseOpAlphaPulseRows.push({
            ...h.tooltipRow,
            lon,
            lat,
            threat_detected: 'Yes'
          });
          continue;
        }
      }
      exerciseHeadsForDots.push(h);
    }
    return { exerciseOpAlphaPulseRows, exerciseHeadsForDots };
  }, [mapLayerVisibility.exerciseScenario, exerciseVesselDeckData.heads]);

  /** Dashed ring only when threat contact and a defense USV are within CONVERGENCE_MAX_M (co-located). */
  const exerciseThreatDefenseConvergence = useMemo(() => {
    if (!mapLayerVisibility.exerciseScenario) return [];
    const heads = exerciseVesselDeckData.heads;
    const threatId = EXERCISE_OP_ALPHA_PROXIMITY_THREAT_VESSEL_ID;
    const maxM = EXERCISE_THREAT_DEFENSE_CONVERGENCE_MAX_M;
    const threatHead = heads.find(h => String(h.id) === threatId);
    if (!threatHead) return [];
    const [tLon, tLat] = threatHead.position;
    let bestM = Infinity;
    let dLon;
    let dLat;
    let defenseLabel = '';
    for (const h of heads) {
      if (String(h.id) === threatId) continue;
      if (!isExerciseDefenseHeadOrPath(h)) continue;
      const [lon, lat] = h.position;
      const m = haversineDistanceMeters(tLat, tLon, lat, lon);
      if (m <= maxM && m < bestM) {
        bestM = m;
        dLon = lon;
        dLat = lat;
        defenseLabel = h.label || 'defense USV';
      }
    }
    if (dLon == null || dLat == null) return [];
    const midLon = (tLon + dLon) / 2;
    const midLat = (tLat + dLat) / 2;
    return [
      {
        path: geodesicCirclePathLonLat(
          midLon,
          midLat,
          EXERCISE_THREAT_DEFENSE_CONVERGENCE_RING_RADIUS_M
        ),
        details: `Converged (~${Math.round(bestM)} m) — threat contact ↔ ${defenseLabel}`
      }
    ];
  }, [mapLayerVisibility.exerciseScenario, exerciseVesselDeckData.heads]);

  useEffect(() => {
    if (!mapLayerVisibility.exerciseScenario) {
      invalidateCommsBurstTimers();
      setCommsBurst(null);
      prevScenarioCommsStepRef.current = 0;
      wasExerciseVesselVisibleRef.current = false;
      return;
    }
    if (!mapContainerReady || !mapRef.current) return;

    const exerciseOn = mapLayerVisibility.exerciseScenario;
    const playbackMs = playbackTime != null && !Number.isNaN(playbackTime) ? playbackTime : null;
    const step = scenarioCommsVisibleStep;

    if (step <= 0) {
      invalidateCommsBurstTimers();
      setCommsBurst(null);
      prevScenarioCommsStepRef.current = 0;
      wasExerciseVesselVisibleRef.current = false;
      return;
    }

    const msgIndex = step - 1;
    const msg = SCENARIO_COMMS_SCRIPT[msgIndex];
    if (!msg) return;

    const endsPrecheck = getScenarioCommsMessageEndpoints(msg, rawData, playbackMs);
    let shouldFire = false;
    if (exerciseOn) {
      const fixOk = hasExerciseVesselFixAtPlayback(rawData, playbackMs);
      const canPlaceLine = endsPrecheck != null;
      const vesselsVisible = fixOk || canPlaceLine;
      if (!vesselsVisible) {
        invalidateCommsBurstTimers();
        setCommsBurst(null);
        prevScenarioCommsStepRef.current = step;
        wasExerciseVesselVisibleRef.current = false;
        return;
      }
      const vesselsJustAppeared = !wasExerciseVesselVisibleRef.current;
      wasExerciseVesselVisibleRef.current = true;
      shouldFire = vesselsJustAppeared || step !== prevScenarioCommsStepRef.current;
    } else {
      wasExerciseVesselVisibleRef.current = false;
      shouldFire = step !== prevScenarioCommsStepRef.current;
    }

    if (!shouldFire) {
      prevScenarioCommsStepRef.current = step;
      return;
    }

    const ends = endsPrecheck;
    if (!ends) return;

    let p1;
    let p2;
    try {
      p1 = mapRef.current.project(ends.fromLL);
      p2 = mapRef.current.project(ends.toLL);
    } catch (_) {
      return;
    }
    if (!p1 || !p2 || typeof p1.x !== 'number' || typeof p2.x !== 'number') return;

    // Only mark this step consumed after layout succeeds; otherwise play would advance
    // time without ever showing comms until the user scrubs (new step / retry).
    prevScenarioCommsStepRef.current = step;

    invalidateCommsBurstTimers();
    const gen = commsBurstGenRef.current;

    const ls = SCENARIO_COMMS_LINK_STYLES[msg.channel] || SCENARIO_COMMS_LINK_STYLES['moc-command'];

    const fullLen = msg.text.length;
    const tickMs = Math.min(38, Math.max(14, Math.floor(2200 / Math.max(1, fullLen))));

    const finishStream = () => {
      if (commsBurstGenRef.current !== gen) return;
      setCommsBurst((b) => {
        if (!b || b.msgIndex !== msgIndex) return b;
        return { ...b, displayedLen: fullLen, phase: 'hold' };
      });
      commsHoldTimeoutRef.current = setTimeout(() => {
        if (commsBurstGenRef.current !== gen) return;
        setCommsBurst((b) => {
          if (!b || b.msgIndex !== msgIndex) return b;
          return { ...b, phase: 'out' };
        });
        commsHideTimeoutRef.current = setTimeout(() => {
          if (commsBurstGenRef.current !== gen) return;
          setCommsBurst(null);
        }, 420);
      }, 720);
    };

    setCommsBurst({
      msg,
      msgIndex,
      fromLL: ends.fromLL,
      toLL: ends.toLL,
      strokeMain: ls.main,
      strokeSoft: ls.soft,
      displayedLen: 0,
      phase: 'stream'
    });

    if (fullLen === 0) {
      finishStream();
      return;
    }

    let nextLen = 0;
    commsStreamIntervalRef.current = setInterval(() => {
      if (commsBurstGenRef.current !== gen) return;
      nextLen += 1;
      if (nextLen >= fullLen) {
        if (commsStreamIntervalRef.current) {
          clearInterval(commsStreamIntervalRef.current);
          commsStreamIntervalRef.current = null;
        }
        finishStream();
        return;
      }
      setCommsBurst((b) => {
        if (!b || b.msgIndex !== msgIndex) return b;
        return { ...b, displayedLen: nextLen };
      });
    }, tickMs);
  }, [
    mapLayerVisibility.exerciseScenario,
    mapContainerReady,
    scenarioCommsVisibleStep,
    rawData,
    playbackTime,
    invalidateCommsBurstTimers
  ]);

  // Re-project comms line/bubble on every map transform while a burst is showing (pan/zoom/flyTo).
  useEffect(() => {
    if (!commsBurst || !mapRef.current) return undefined;
    const m = mapRef.current;
    const bump = () => {
      if (commsMoveRafRef.current != null) return;
      commsMoveRafRef.current = requestAnimationFrame(() => {
        commsMoveRafRef.current = null;
        setCommsMapMoveSerial((c) => c + 1);
      });
    };
    bump();
    m.on('move', bump);
    return () => {
      m.off('move', bump);
      if (commsMoveRafRef.current != null) {
        cancelAnimationFrame(commsMoveRafRef.current);
        commsMoveRafRef.current = null;
      }
    };
  }, [commsBurst, mapContainerReady]);

  // Re-project on every render when map/view or burst changes (useMemo deps tripped exhaustive-deps in CI: mapRef + project() are opaque to the linter).
  const commsBurstLayoutPx =
    commsBurst && mapRef.current
      ? computeScenarioCommsLinkLayoutPx(
          mapRef.current,
          commsBurst.fromLL,
          commsBurst.toLL,
          commsBurst.strokeMain,
          commsBurst.strokeSoft
        )
      : null;

  // Keep exercise comms panel text streaming in lockstep with map overlay
  useEffect(() => {
    setScenarioCommsBurst(commsBurst);
  }, [commsBurst, setScenarioCommsBurst]);

  useEffect(
    () => () => {
      setScenarioCommsBurst(null);
    },
    [setScenarioCommsBurst]
  );

  const oceanCurrentData = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];
    return rawData.filter(d =>
      d.lat != null &&
      d.lon != null &&
      !isNaN(d.lat) &&
      !isNaN(d.lon) &&
      (d.nspeed != null || d.speed != null) && d.direction != null
    );
  }, [rawData]);



  // Particle animation state (reserved for future use)
  // eslint-disable-next-line no-unused-vars
  const [_animationDebugInfo] = useState({ particleCount: 0, frame: 0, dataChanged: false });

  // NEW: Determine if particle animations should be paused
  // Only pause if hovering a specific station, NOT for general coordinate data availability
  // Hide coordinate/data-availability tooltip when zoomed in (zoom > 9) to avoid blocking vessel view
  const activeTooltip = hoveredStation || (viewState.zoom <= 9 ? coordinateHover : null);
  const pauseParticleAnimations = Boolean(hoveredStation);

  const viewModeRef = useRef(viewMode);

  // Data source for vessel layers: one vessel when selected, otherwise all rawData; then apply threat + vessel-type filters
  const vesselDataSource = useMemo(() => {
    let base = [];
    if (selectedVesselId === VESSEL_DISPLAY_NONE) {
      base = [];
    } else if (selectedVesselId && vesselsById && vesselsById.has(selectedVesselId)) {
      base = vesselsById.get(selectedVesselId) || [];
    } else {
      base = rawData || [];
    }
    if (base.length === 0) return base;
    // Only filter by threat when explicitly 'threat' or 'no-threat'; treat 'all' and any other value as show-all
    if (threatLevelFilter === 'threat' || threatLevelFilter === 'no-threat') {
      const wantThreat = threatLevelFilter === 'threat';
      base = base.filter((d) => {
        const threat = d && (d.threat_detected === 'Yes' || d.threat_detected === true);
        return threat === wantThreat;
      });
    }
    const typeFilterArr = Array.isArray(vesselTypeFilter) ? vesselTypeFilter : [];
    if (typeFilterArr.length > 0) {
      base = base.filter((d) => {
        const name = (d && (d.common_name ?? d.message_description_label ?? d.message_description_name)) ?? '';
        return typeFilterArr.includes(String(name).trim());
      });
    }
    return base;
  }, [rawData, selectedVesselId, vesselsById, threatLevelFilter, vesselTypeFilter]);

  // Fly map to vessel data when user switches to showing vessels so they can see where vessels are
  useEffect(() => {
    const isVesselView = viewMode === 'Vessel Tracking' || viewMode === 'Relative Grid View';
    if (!isVesselView || selectedVesselId === VESSEL_DISPLAY_NONE || !vesselDataSource || vesselDataSource.length === 0 || !mapRef.current || !mapContainerReady) return;

    let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
    vesselDataSource.forEach((d) => {
      const lon = d.lon ?? d.longitude;
      const lat = d.lat ?? d.latitude;
      if (typeof lon === 'number' && typeof lat === 'number' && !Number.isNaN(lon) && !Number.isNaN(lat)) {
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      }
    });
    if (minLon === Infinity || minLat === Infinity) return;

    const centerLon = (minLon + maxLon) / 2;
    const centerLat = (minLat + maxLat) / 2;
    const lonSpan = Math.abs(maxLon - minLon);
    const latSpan = Math.abs(maxLat - minLat);
    const span = Math.max(lonSpan, latSpan, 0.001);
    let targetZoom = 12;
    if (span < 0.05) targetZoom = 13;
    else if (span < 0.1) targetZoom = 12;
    else if (span < 0.3) targetZoom = 11;
    else if (span < 1.0) targetZoom = 10;
    else targetZoom = 8;
    const MAX_ZOOM = 15;
    targetZoom = Math.min(MAX_ZOOM, targetZoom + 2);

    mapRef.current.flyTo({
      center: [centerLon, centerLat],
      zoom: targetZoom,
      duration: 1500
    });
    setViewState((prev) => ({
      ...prev,
      longitude: centerLon,
      latitude: centerLat,
      zoom: targetZoom
    }));
  }, [viewMode, selectedVesselId, vesselDataSource, mapContainerReady]);

  // Keep the viewMode ref up to date for the Mapbox style.load event listener
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);
  // Clear coordinate/data-availability tooltip when zoomed in so it doesn't obscure the map
  useEffect(() => {
    if (viewState.zoom > 9) {
      coordinateHoverSigRef.current = '';
      setCoordinateHover((p) => (p == null ? p : null));
    }
  }, [viewState.zoom]);

  // Max zoom: disable "zoom in" 3 levels before tile limit so "Map data not yet available" never shows
  const MAP_ZOOM_TILE_LIMIT = 18; // zoom level where placeholder appears
  const MAP_MAX_ZOOM = Math.max(1, MAP_ZOOM_TILE_LIMIT - 3); // 15: zoom-in disables here
  // Hide "Map data not yet available" overlay (Mapbox placeholder) whenever it appears
  const hideMapDataPlaceholder = useCallback(() => {
    const walk = (el) => {
      if (!el || el.nodeType !== 1) return;
      const text = (el.textContent || '').trim();
      const isPlaceholder = text && (
        text.includes('Map data not yet available') ||
        (text.includes('Map data') && text.includes('not yet available')) ||
        text.includes('Map data not') // truncated or partial (e.g. when zoom-in is disabled at max zoom)
      );
      if (isPlaceholder) {
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('display', 'none', 'important');
      }
      for (let i = 0; i < el.childNodes.length; i++) walk(el.childNodes[i]);
    };
    if (mapContainerRef.current) walk(mapContainerRef.current);
    walk(document.body);
  }, []);

  useEffect(() => {
    if (!mapContainerReady) return;
    hideMapDataPlaceholder();
    const interval = setInterval(hideMapDataPlaceholder, 400);
    const container = mapContainerRef.current;
    if (container) {
      const observer = new MutationObserver(() => { hideMapDataPlaceholder(); });
      observer.observe(container, { childList: true, subtree: true, characterData: true, characterDataOldValue: true });
      return () => {
        clearInterval(interval);
        observer.disconnect();
      };
    }
    return () => clearInterval(interval);
  }, [mapContainerReady, hideMapDataPlaceholder, viewState.zoom]);

  // Effect: Prepare Vessel Data (uses vesselDataSource so selection filters tracks)
  useEffect(() => {
    const exerciseClockFallback =
      mapLayerVisibility.exerciseScenario &&
      rawData &&
      rawData.length >= 2 &&
      (!vesselDataSource || vesselDataSource.length === 0);

    if (vesselDataSource && vesselDataSource.length > 0) {
      const sorted = sortVesselData(vesselDataSource);
      sortedVesselDataRef.current = sorted;

      // Group into tracks and tag each point with its track index
      const clusteredTracks = clusterVesselData(sorted);
      clusteredTracks.forEach((track, idx) => {
        track.forEach(point => {
          point.trackId = idx;
        });
      });
      setClusteredVesselData(clusteredTracks);

      // Initialize internal playback time only when not controlled by parent (no setPlaybackTimeProp)
      if (setPlaybackTimeProp === undefined && sorted.length > 0) {
        setInternalPlaybackTime(new Date(sorted[0].timestamp || sorted[0].time).getTime());
      }
    } else if (exerciseClockFallback) {
      // Same chronological span as useOceanData story clock (exercise rows only when enough exist).
      const sorted = sortedExerciseStoryRowsFromRaw(rawData);
      sortedVesselDataRef.current = sorted;
      setClusteredVesselData([]);
      if (setPlaybackTimeProp === undefined && sorted.length > 0) {
        setInternalPlaybackTime(new Date(sorted[0].timestamp || sorted[0].time).getTime());
      }
    } else {
      sortedVesselDataRef.current = [];
      setClusteredVesselData([]);
      if (setPlaybackTimeProp === undefined) setInternalPlaybackTime(null);
    }
  }, [vesselDataSource, setPlaybackTimeProp, mapLayerVisibility.exerciseScenario, rawData]);

  // When Play starts, keep playback inside the sorted ref window (exercise fallback can otherwise sit before first row).
  useEffect(() => {
    if (!isPlaying) return;
    if (!isVesselMapPlaybackViewMode(viewMode)) return;
    const sorted = sortedVesselDataRef.current;
    if (!sorted || sorted.length < 2) return;
    const startTime = new Date(sorted[0].timestamp || sorted[0].time).getTime();
    const endTime = new Date(sorted[sorted.length - 1].timestamp || sorted[sorted.length - 1].time).getTime();
    if (Number.isNaN(startTime) || Number.isNaN(endTime)) return;
    setPlaybackTime((t) => {
      if (t == null || Number.isNaN(t)) return t;
      if (t < startTime) return startTime;
      if (t > endTime) return endTime;
      return t;
    });
  }, [isPlaying, viewMode, setPlaybackTime]);

  // Derive current vessel state for rendering (use state so we re-render when clustered data is ready)
  const vesselStates = useMemo(() => {
    if (!playbackTime || !clusteredVesselData || clusteredVesselData.length === 0) {
      return null;
    }
    return interpolateMultipleVesselStates(clusteredVesselData, playbackTime);
  }, [playbackTime, clusteredVesselData]);

  // Resolve popup position and content: follows vessel during playback and stays on vessel when zooming/panning
  /* eslint-disable-next-line no-unused-vars */
  const _trackInfoResolved = useMemo(() => {
    if (!trackInfo) return null;
    const obj = trackInfo.object;
    const trackId = obj.trackId ?? 0;
    const currentVessel = vesselStates && vesselStates.length > 0
      ? vesselStates.find(v => (v.trackId ?? 0) === trackId)
      : null;
    const displayObject = currentVessel || obj;
    const vesselToProject = currentVessel || obj;
    let x = trackInfo.x;
    let y = trackInfo.y;
    if (mapRef.current && vesselToProject.lat != null && vesselToProject.lon != null) {
      try {
        const pt = mapRef.current.project([vesselToProject.lon, vesselToProject.lat]);
        if (pt && typeof pt.x === 'number' && typeof pt.y === 'number') {
          x = pt.x;
          y = pt.y;
        }
      } catch (_) { /* fallback to click position */ }
    }
    return { x, y, object: displayObject };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- viewState excluded to avoid recompute on every pan/zoom
  }, [trackInfo, vesselStates]);

  /** 0/1 so the vessel play effect does not re-subscribe every time playbackTime ms advances. */
  const vesselPlaybackClockReady =
    playbackTime != null && !Number.isNaN(Number(playbackTime)) ? 1 : 0;

  // Effect: Animation loop — vessel timeline views (incl. Threat Zone / Marker Clusters; see storyTimeUtils).
  useEffect(() => {
    const hasVesselData = sortedVesselDataRef.current.length >= 2;
    const hasPlaybackTime = playbackTime != null;

    if (!isVesselMapPlaybackViewMode(viewMode) || !isPlaying || !hasVesselData || !hasPlaybackTime) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      lastFrameTimeRef.current = null;
      return;
    }

    // Use setInterval instead of rAF to avoid "Maximum update depth exceeded" (setState every frame).
    const intervalMs = 50;
    lastFrameTimeRef.current = Date.now();
    const id = setInterval(() => {
      if (!isPlayingRef.current) return;
      const now = Date.now();
      const deltaTime = lastFrameTimeRef.current ? (now - lastFrameTimeRef.current) : 0;
      lastFrameTimeRef.current = now;
      const timeAdvance = deltaTime * playbackSpeed * 50;

      setPlaybackTime((prevTime) => {
        if (prevTime === null) return null;
        const sorted = sortedVesselDataRef.current;
        if (!sorted || sorted.length === 0) return prevTime;
        const endTime = new Date(sorted[sorted.length - 1].timestamp || sorted[sorted.length - 1].time).getTime();
        const startTime = new Date(sorted[0].timestamp || sorted[0].time).getTime();
        // When playback was at end and user clicked play again, reset to start so animation runs from beginning
        if (prevTime >= endTime - 1) {
          notifyPlaybackReset();
          return startTime;
        }
        let nextTime = prevTime + timeAdvance;
        if (nextTime >= endTime) {
          if (loopMode === 'Once') {
            if (typeof onPausePlayback === 'function') onPausePlayback();
            return endTime;
          }
          notifyPlaybackReset();
          nextTime = startTime;
        }
        return nextTime;
      });
    }, intervalMs);

    return () => clearInterval(id);
  // Re-run when the clock becomes valid or tracks load while Play is on; isPlayingRef blocks ticks after Pause.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setPlaybackTime stable from parent
  }, [
    isPlaying,
    playbackSpeed,
    viewMode,
    loopMode,
    onPausePlayback,
    vesselPlaybackClockReady,
    clusteredVesselData.length,
    notifyPlaybackReset
  ]);



  // Station data processing - only use valid stations from props, no fallback
  const finalStationData = useMemo(() => {
    if (stationData.length > 0) {
      const validStations = stationData.filter(station => {
        if (!station.coordinates || station.coordinates.length !== 2) return false;
        const [lon, lat] = station.coordinates;
        return lon !== null && lat !== null && !isNaN(lon) && !isNaN(lat) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
      });

      if (validStations.length > 0 && mapRef.current) {
        const lons = validStations.map(s => s.coordinates[0]);
        const lats = validStations.map(s => s.coordinates[1]);
        const bounds = [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]];
        const currentCenter = mapRef.current.getCenter();
        const centerLon = (bounds[0][0] + bounds[1][0]) / 2;
        const centerLat = (bounds[0][1] + bounds[1][1]) / 2;
        const distance = Math.sqrt(Math.pow(currentCenter.lng - centerLon, 2) + Math.pow(currentCenter.lat - centerLat, 2));

        if (distance > 1) {
          setTimeout(() => {
            if (mapRef.current) mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 12 });
          }, 1000);
        }
      }
      return validStations;
    }

    return [];
  }, [stationData]);







  const checkDataAvailability = useMemo(() => {
    return (longitude, latitude) => {
      const availableData = [];
      const searchRadius = 0.1;

      // Check station data
      const nearbyStations = finalStationData.filter(station => {
        if (!station.coordinates) return false;
        const [stationLon, stationLat] = station.coordinates;
        const distance = Math.sqrt(
          Math.pow(longitude - stationLon, 2) + Math.pow(latitude - stationLat, 2)
        );
        return distance <= searchRadius;
      });

      if (nearbyStations.length > 0) {
        const totalDataPoints = nearbyStations.reduce((sum, station) => sum + (station.dataPoints || 0), 0);
        availableData.push(`${nearbyStations.length} Station${nearbyStations.length > 1 ? 's' : ''} (${totalDataPoints} measurements)`);
      }

      // Check raw data availability
      if (rawData && rawData.length > 0) {
        const nearbyRawData = rawData.filter(data => {
          if (!data.lon || !data.lat) return false;
          const distance = Math.sqrt(
            Math.pow(longitude - data.lon, 2) + Math.pow(latitude - data.lat, 2)
          );
          return distance <= searchRadius;
        });

        if (nearbyRawData.length > 0) {
          const latestData = nearbyRawData
            .filter(d => d.time)
            .sort((a, b) => new Date(b.time) - new Date(a.time))[0];

          if (latestData) {
            if (latestData.nspeed != null) availableData.push(`Wind Speed: ${latestData.nspeed.toFixed(2)} m/s`);
            if (latestData.direction != null) availableData.push(`Current Direction: ${latestData.direction.toFixed(0)}°`);
            if (latestData.temp != null) availableData.push(`Temperature: ${latestData.temp.toFixed(1)}°C`);
            if (latestData.salinity != null) availableData.push(`Salinity: ${latestData.salinity.toFixed(1)} PSU`);
            if (latestData.ssh != null) availableData.push(`SSH: ${latestData.ssh.toFixed(2)} m`);
            if (latestData.pressure_dbars != null) availableData.push(`Pressure: ${latestData.pressure_dbars.toFixed(1)} dbar`);
            if (latestData.depth != null) availableData.push(`Depth: ${latestData.depth.toFixed(0)} m`);
          } else {
            availableData.push(`Ocean Data (${nearbyRawData.length} measurements)`);
          }
        }
      }

      if (mapLayerVisibility.temperature && temperatureHeatmapData.length > 0) {
        const nearbyTempData = temperatureHeatmapData.filter(point => {
          const [pointLat, pointLon] = point;
          const distance = Math.sqrt(
            Math.pow(longitude - pointLon, 2) + Math.pow(latitude - pointLat, 2)
          );
          return distance <= searchRadius;
        });
        if (nearbyTempData.length > 0) {
          availableData.push(`Temperature Heatmap Active`);
        }
      }

      if (mapLayerVisibility.salinity && salinityHeatmapData.length > 0) {
        const nearbySal = salinityHeatmapData.filter(point => {
          const [pointLat, pointLon] = point;
          const distance = Math.sqrt(
            Math.pow(longitude - pointLon, 2) + Math.pow(latitude - pointLat, 2)
          );
          return distance <= searchRadius;
        });
        if (nearbySal.length > 0) {
          availableData.push(`Salinity Heatmap Active`);
        }
      }

      return availableData;
    };
  }, [finalStationData, rawData, temperatureHeatmapData, salinityHeatmapData, mapLayerVisibility]);

  // Set Mapbox access token
  useEffect(() => {
    if (mapboxToken) {
      mapboxgl.accessToken = mapboxToken;
    } else {
      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
        'pk.eyJ1Ijoiam1wYXVsbWFwYm94IiwiYSI6ImNtZHh0ZmR6MjFoaHIyam9vZmJ4Z2x1MDYifQ.gR60szhfKWhTv8MyqynpVA';
    }
  }, [mapboxToken]);

  // Resize map synchronously before paint when the output column width changes.
  useLayoutEffect(() => {
    mapRef.current?.resize();
  }, [isOutputCollapsed, outputPanelWidth, isOutputPanelResizing]);

  // Handle SSH layer pitch changes
  useEffect(() => {
    if (!mapRef.current) return;

    const targetPitch = mapLayerVisibility.ssh ? 30 : 0;

    // Only change pitch if it's different from current pitch
    if (Math.abs(mapRef.current.getPitch() - targetPitch) > 1) {
      mapRef.current.easeTo({
        pitch: targetPitch,
        duration: 1000,
        easing: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
      });

      // Update viewState to reflect the pitch change
      setViewState(prev => ({ ...prev, pitch: targetPitch }));
    }
  }, [mapLayerVisibility.ssh]);

  // Helper function to get the appropriate base style for ArcGIS ocean layer
  const getBaseStyleForOcean = () => {
    return 'mapbox://styles/mapbox/light-v10'; // Use light style as base for ocean layer
  };

  // Handle map style changes
  const handleMapStyleChange = (newStyle) => {
    if (!mapRef.current) return;

    setMapStyle(newStyle);
    currentMapStyleRef.current = newStyle; // Update ref immediately

    if (viewMode === 'Relative Grid View') {
      mapRef.current.setStyle(RELATIVE_GRID_STYLE);
    } else if (newStyle === 'arcgis-ocean') {
      // For ArcGIS ocean style, use light base and show ocean layer
      mapRef.current.setStyle(getBaseStyleForOcean());
    } else {
      // For other styles, set the style (ocean layer won't be added)
      mapRef.current.setStyle(newStyle);
    }
  };

  useEffect(() => {
    if (!mapRef.current) return;

    try {
      if (viewMode === 'Relative Grid View') {
        mapRef.current.setStyle(RELATIVE_GRID_STYLE);
      } else {
        const nextStyle = mapStyle === 'arcgis-ocean' ? getBaseStyleForOcean() : mapStyle;
        mapRef.current.setStyle(nextStyle);
      }
    } catch (error) {
      console.error(`[MapContext Debug] Error calling setStyle:`, error);
    }
  }, [viewMode, mapStyle]);

  // Generate coordinate grid lines
  const generateGridData = useMemo(() => {
    if (!showGrid) return [];

    const gridLines = [];
    const bounds = finalStationData.length > 0 ? {
      minLon: Math.min(...finalStationData.map(s => s.coordinates[0])) - 5,
      maxLon: Math.max(...finalStationData.map(s => s.coordinates[0])) + 5,
      minLat: Math.min(...finalStationData.map(s => s.coordinates[1])) - 5,
      maxLat: Math.max(...finalStationData.map(s => s.coordinates[1])) + 5
    } : { minLon: -180, maxLon: 180, minLat: -85, maxLat: 85 };

    bounds.minLon = Math.max(-180, bounds.minLon);
    bounds.maxLon = Math.min(180, bounds.maxLon);
    bounds.minLat = Math.max(-85, bounds.minLat);
    bounds.maxLat = Math.min(85, bounds.maxLat);

    const lonStart = Math.floor(bounds.minLon / gridSpacing) * gridSpacing;
    const lonEnd = Math.ceil(bounds.maxLon / gridSpacing) * gridSpacing;

    for (let lon = lonStart; lon <= lonEnd; lon += gridSpacing) {
      if (lon >= bounds.minLon && lon <= bounds.maxLon) {
        gridLines.push({ type: 'longitude', value: lon, sourcePosition: [lon, bounds.minLat], targetPosition: [lon, bounds.maxLat], color: gridColor });
      }
    }

    const latStart = Math.floor(bounds.minLat / gridSpacing) * gridSpacing;
    const latEnd = Math.ceil(bounds.maxLat / gridSpacing) * gridSpacing;

    for (let lat = latStart; lat <= latEnd; lat += gridSpacing) {
      if (lat >= bounds.minLat && lat <= bounds.maxLat) {
        gridLines.push({ type: 'latitude', value: lat, sourcePosition: [bounds.minLon, lat], targetPosition: [bounds.maxLon, lat], color: gridColor });
      }
    }
    return gridLines;
  }, [showGrid, gridSpacing, gridColor, finalStationData]);

  const spinGlobe = () => {
    if (!mapRef.current) return;
    const zoom = mapRef.current.getZoom();
    const secondsPerRevolution = 240;
    const maxSpinZoom = 5;
    const slowSpinZoom = 3;

    if (spinEnabled && !userInteracting && zoom < maxSpinZoom) {
      let distancePerSecond = 360 / secondsPerRevolution;
      if (zoom > slowSpinZoom) {
        distancePerSecond *= (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
      }
      const center = mapRef.current.getCenter();
      center.lng -= distancePerSecond;
      mapRef.current.easeTo({ center, duration: 1000, easing: (n) => n });
    }
  };

  useEffect(() => {
    if (!mapContainerReady || !mapContainerRef.current || mapRef.current) return;

    // Use the provided initialViewState, no fallback to hardcoded values
    const startingViewState = initialViewState;

    // Set initial style based on mapStyle state and viewMode
    const initialStyle = viewModeRef.current === 'Relative Grid View'
      ? RELATIVE_GRID_STYLE
      : (mapStyle === 'arcgis-ocean' ? getBaseStyleForOcean() : mapStyle);

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: initialStyle,
      center: [startingViewState.longitude, startingViewState.latitude],
      projection: 'globe',
      zoom: startingViewState.zoom,
      pitch: startingViewState.pitch,
      bearing: startingViewState.bearing,
      antialias: true,
      maxZoom: MAP_MAX_ZOOM
    });
    setMapInstanceReady(true);
    mapRef.current.addControl(new mapboxgl.NavigationControl());
    mapRef.current.on('style.load', () => {
      mapRef.current.setFog({});

      // Only add ocean layer if we're using the arcgis-ocean style AND NOT in Relative Grid View
      if (currentMapStyleRef.current === 'arcgis-ocean' && viewModeRef.current !== 'Relative Grid View') {
        const oceanSourceId = 'arcgis-ocean-source';
        const oceanLayerId = 'arcgis-ocean-layer';

        if (!mapRef.current.getSource(oceanSourceId)) {
          mapRef.current.addSource(oceanSourceId, {
            'type': 'raster',
            'tiles': [
              'https://services.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}'
            ],
            'tileSize': 256
          });
        }

        const layers = mapRef.current.getStyle().layers;
        let firstSymbolId;
        for (const layer of layers) {
          if (layer.type === 'symbol') {
            firstSymbolId = layer.id;
            break;
          }
        }

        if (!mapRef.current.getLayer(oceanLayerId)) {
          mapRef.current.addLayer({
            'id': oceanLayerId,
            'type': 'raster',
            'source': oceanSourceId,
            'paint': {
              'raster-opacity': 1.0
            },
            'layout': {
              'visibility': 'visible'
            }
          }, firstSymbolId);
        }
      }
    });
    mapRef.current.on('error', (e) => console.error('Map error:', e));
    mapRef.current.on('mousedown', () => setUserInteracting(true));
    mapRef.current.on('dragstart', () => setUserInteracting(true));
    mapRef.current.on('moveend', () => {
      if (mapRef.current) {
        const { lng, lat } = mapRef.current.getCenter();
        setViewState({
          longitude: lng, latitude: lat, zoom: mapRef.current.getZoom(),
          pitch: mapRef.current.getPitch(), bearing: mapRef.current.getBearing()
        });
      }
      setTimeout(() => { setUserInteracting(false); spinGlobe(); }, 1000);
    });
    if (spinEnabled) spinGlobe();
    return () => {
      setMapInstanceReady(false);
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initial setup only; MAP_MAX_ZOOM, initialViewState, mapStyle, spinGlobe are stable
  }, [mapContainerReady, spinEnabled]);

  // Memoize deck layer list so DeckGL is not fed new Layer instances every render (avoids hover/render loops).
  const deckLayers = useMemo(() => {
    const layers = [];
    // Demo vessel icons (PNG atlas) are only meaningful with Exercise scenario; otherwise force dots for fleet views.
    const fleetMarkerMode = mapLayerVisibility.exerciseScenario ? vesselMarkerDisplay : 'dots';
    let suppressGeographicThreatPulse = false;
    const { pulseIntensity, radiusAnimation, colorPulse } = animationValues;
    const threatPulseActive = !!layerAnimationEnabled.threats && isPlaying;

    // Grids
    if ((showGrid || viewMode === 'Relative Grid View') && generateGridData.length > 0) {
      layers.push(new LineLayer({
        id: 'coordinate-grid', data: generateGridData, getSourcePosition: d => d.sourcePosition,
        getTargetPosition: d => d.targetPosition, getColor: d => d.color,
        getWidth: d => d.type === 'latitude' && Math.abs(d.value) < 0.001 ? 3 : 1,
        widthScale: 1, widthMinPixels: 0.5, widthMaxPixels: 2, opacity: gridOpacity,
        pickable: true, autoHighlight: false,
        onHover: ({ object, x, y }) => {
          if (object && viewState.zoom > 4) {
            const label = object.type === 'latitude' ? `${Math.abs(object.value)}°${object.value >= 0 ? 'N' : 'S'}` : `${Math.abs(object.value)}°${object.value >= 0 ? 'E' : 'W'}`;
            setHoveredStation({ name: `Grid Line`, details: `${object.type === 'latitude' ? 'Latitude' : 'Longitude'}: ${label}`, x, y, isGrid: true });
          } else setHoveredStation(null);
        }
      }));
    }

    // SIMPLE: Wind Arrow Vector Field with pulsing animation
    if (mapLayerVisibility.windVelocity && windSourceData.length > 0) {
      const windArrows = generateArrowVectorData(windSourceData, {
        sampleRate: 3, // Use every 3rd point
        arrowLength: 0.04,
        speedScale: 1.2,
        type: 'wind'
      });

      // Arrow lines showing wind direction (with pulsing width and color)
      layers.push(new LineLayer({
        id: 'wind-vector-arrows',
        data: windArrows,
        getSourcePosition: d => d.sourcePosition,
        getTargetPosition: d => d.targetPosition,
        // Dynamic color pulsing - brightens/dims with colorPulse
        getColor: d => d.color ? [
          Math.min(255, Math.round(d.color[0] * colorPulse)),
          Math.min(255, Math.round(d.color[1] * colorPulse)),
          Math.min(255, Math.round(d.color[2] * colorPulse)),
          d.color[3]
        ] : [200, 200, 255, 200],
        getWidth: d => (2 + d.speed * 0.5) * pulseIntensity,
        widthMinPixels: 1,
        widthMaxPixels: 5,
        pickable: true,
        opacity: 0.9,
        updateTriggers: {
          getWidth: [pulseIntensity],
          getColor: [colorPulse]
        }
      }));

      // Triangular arrow heads at tip
      layers.push(new IconLayer({
        id: 'wind-vector-heads',
        data: windArrows,
        iconAtlas: ARROW_ICON_ATLAS,
        iconMapping: ARROW_ICON_MAPPING,
        getIcon: () => 'arrow',
        getPosition: d => d.targetPosition,
        getSize: d => 18 + d.speed * 8,
        sizeScale: radiusAnimation,
        sizeMinPixels: 8,
        sizeMaxPixels: 40,
        getAngle: d => -d.direction + 45, // Adjust for icon rotation
        getColor: d => d.color,
        billboard: false,
        pickable: true,
        opacity: 0.9,
        updateTriggers: {
          getSize: [radiusAnimation],
          getAngle: [d => d.direction]
        }
      }));
    }

    // SIMPLE: Ocean Currents Arrow Vector Field with pulsing animation
    if (mapLayerVisibility.oceanCurrents && oceanCurrentData.length > 0) {
      const currentArrows = generateArrowVectorData(oceanCurrentData, {
        sampleRate: 3, // Use every 3rd point
        arrowLength: 0.03,
        speedScale: 1.0,
        type: 'currents'
      });

      // Arrow lines showing current direction
      layers.push(new LineLayer({
        id: 'ocean-currents-vector-arrows',
        data: currentArrows,
        getSourcePosition: d => d.sourcePosition,
        getTargetPosition: d => d.targetPosition,
        getColor: d => d.color ? [
          Math.min(255, Math.round(d.color[0] * colorPulse)),
          Math.min(255, Math.round(d.color[1] * colorPulse)),
          Math.min(255, Math.round(d.color[2] * colorPulse)),
          d.color[3]
        ] : [100, 100, 255, 200],
        getWidth: d => (2 + d.speed * 0.8) * pulseIntensity,
        widthMinPixels: 1,
        widthMaxPixels: 6,
        pickable: true,
        opacity: 0.9,
        updateTriggers: {
          getWidth: [pulseIntensity],
          getColor: [colorPulse]
        }
      }));

      // Triangular arrow heads at tip
      layers.push(new IconLayer({
        id: 'ocean-currents-vector-heads',
        data: currentArrows,
        iconAtlas: ARROW_ICON_ATLAS,
        iconMapping: ARROW_ICON_MAPPING,
        getIcon: () => 'arrow',
        getPosition: d => d.targetPosition,
        getSize: d => 18 + d.speed * 12,
        sizeScale: radiusAnimation,
        sizeMinPixels: 10,
        sizeMaxPixels: 50,
        getAngle: d => -d.direction + 45, // Adjust for icon rotation
        getColor: d => d.color,
        billboard: false,
        pickable: true,
        opacity: 0.9,
        updateTriggers: {
          getSize: [radiusAnimation],
          getAngle: [d => d.direction]
        }
      }));
    }

    // Animated Temperature Layer
    if (mapLayerVisibility.temperature && temperatureHeatmapData.length > 0) {
      layers.push(new HeatmapLayer({
        id: 'temperature-heatmap-layer',
        data: temperatureHeatmapData,
        getPosition: d => [d[1], d[0]],
        getWeight: d => d[2] * pulseIntensity,
        radiusPixels: (35 + heatmapScale * 30) * radiusAnimation,
        intensity: 1.2 * pulseIntensity * heatmapScale,
        threshold: 0.08,
        aggregation: 'SUM',
        colorRange: TEMPERATURE_COLOR_RANGE,
        updateTriggers: {
          getWeight: [Math.floor(currentFrame / 3)],
          radiusPixels: [Math.floor(currentFrame / 3), heatmapScale],
          intensity: [Math.floor(currentFrame / 3), heatmapScale]
        }
      }));
    }

    // Animated Salinity Layer
    if (mapLayerVisibility.salinity && salinityHeatmapData.length > 0) {
      layers.push(new HeatmapLayer({
        id: 'salinity-heatmap-layer',
        data: salinityHeatmapData,
        getPosition: d => [d[1], d[0]],
        getWeight: d => d[2] * pulseIntensity,
        radiusPixels: (35 + heatmapScale * 30) * radiusAnimation,
        intensity: 1.2 * pulseIntensity * heatmapScale,
        threshold: 0.08,
        aggregation: 'SUM',
        colorRange: SALINITY_COLOR_RANGE,
        updateTriggers: {
          getWeight: [Math.floor(currentFrame / 3)],
          radiusPixels: [Math.floor(currentFrame / 3), heatmapScale],
          intensity: [Math.floor(currentFrame / 3), heatmapScale]
        }
      }));
    }

    // Animated SSH Layer
    if (mapLayerVisibility.ssh && sshHexagonData.length > 0) {
      layers.push(new HexagonLayer({
        id: 'ssh-hexagon-layer',
        data: sshHexagonData,
        getPosition: d => [d.lon, d.lat],
        extruded: true,
        radius: 2500,
        elevationScale: 40 * pulseIntensity,
        getElevationWeight: d => d.ssh,
        aggregation: 'MEAN',
        upperPercentile: 99,
        colorRange: SSH_COLOR_RANGE,
        material: {
          ambient: 0.6,
          diffuse: 0.9,
          shininess: 32,
          specularColor: [51, 51, 51]
        },
        transitions: {
          elevationScale: 1000
        },
        updateTriggers: {
          elevationScale: [pulseIntensity]
        }
      }));
    }

    // Animated Pressure Layer
    if (mapLayerVisibility.pressure && pressureHeatmapData.length > 0) {
      layers.push(new HeatmapLayer({
        id: 'pressure-heatmap-layer',
        data: pressureHeatmapData,
        getPosition: d => [d[1], d[0]],
        getWeight: d => d[2] * pulseIntensity,
        radiusPixels: (35 + heatmapScale * 30) * radiusAnimation,
        intensity: 1.2 * pulseIntensity * heatmapScale,
        threshold: 0.08,
        aggregation: 'SUM',
        colorRange: PRESSURE_COLOR_RANGE,
        updateTriggers: {
          getWeight: [Math.floor(currentFrame / 3)],
          radiusPixels: [Math.floor(currentFrame / 3), heatmapScale],
          intensity: [Math.floor(currentFrame / 3), heatmapScale]
        }
      }));
    }

    // Scripted realistic exercise: Op Alpha, threat polygon, landmarks, illustrative comms lines
    if (mapLayerVisibility.exerciseScenario) {
      layers.push(new PolygonLayer({
        id: 'exercise-scenario-polygons',
        data: SCENARIO_POLYGON_FEATURES,
        getPolygon: d => d.polygon,
        getFillColor: d => d.fill,
        getLineColor: d => d.line,
        getLineWidth: 2,
        lineWidthMinPixels: 2,
        filled: true,
        stroked: true,
        pickable: true,
        onHover: ({ object, x, y }) => {
          if (object) {
            setHoveredStation({
              name: object.name,
              details: 'Exercise overlay (not live API data)',
              x,
              y,
              isGrid: false
            });
          } else {
            setHoveredStation(null);
          }
        }
      }));

      layers.push(new ScatterplotLayer({
        id: 'exercise-moc-ship',
        data: [{
          position: [MOC_SHIP_LON_LAT[0], MOC_SHIP_LON_LAT[1]],
          name: 'Maritime Operations Center'
        }],
        getPosition: d => d.position,
        getFillColor: [...MOC_MARKER_FILL_RGB, 240],
        getLineColor: [...MOC_MARKER_STROKE_RGB, 255],
        getRadius: 11,
        radiusUnits: 'pixels',
        stroked: true,
        lineWidthMinPixels: 2,
        pickable: true,
        onHover: ({ object, x, y }) => {
          if (object) {
            setHoveredStation({
              name: object.name,
              details: 'Command ship — scenario comms hub (outside Op Alpha)',
              x,
              y,
              isGrid: false
            });
          } else {
            setHoveredStation(null);
          }
        }
      }));

      if (exerciseVesselDeckData.paths.length > 0) {
        layers.push(new PathLayer({
          id: 'exercise-vessel-tracks',
          data: exerciseVesselDeckData.paths,
          getPath: d => d.path,
          getColor: d => {
            if (Array.isArray(d.color) && d.color.length >= 4) {
              return d.color;
            }
            const a = 220;
            if (isExerciseAuvHeadOrPath(d)) return [...AUV_VESSEL_DOT_RGB, a];
            if (isExerciseDefenseHeadOrPath(d)) return [...DEFENSIVE_SURFACE_VESSEL_DOT_RGB, a];
            return Array.isArray(d.color) ? [...d.color.slice(0, 3), a] : [128, 128, 128, a];
          },
          getWidth: d => d.width,
          widthUnits: 'pixels',
          widthMinPixels: 2,
          pickable: false
        }));
      }

      if (exerciseThreatDefenseConvergence.length > 0) {
        const onConvergenceHover = ({ object, x, y }) => {
          if (object) {
            setHoveredStation({
              name: 'Convergence zone',
              details: object.details,
              x,
              y,
              isGrid: false
            });
          } else {
            setHoveredStation(null);
          }
        };
        layers.push(
          new PolygonLayer({
            id: 'exercise-threat-defense-convergence-fill',
            data: exerciseThreatDefenseConvergence,
            getPolygon: d => d.path,
            getFillColor: [253, 224, 71, 72],
            filled: true,
            stroked: false,
            pickable: true,
            onHover: onConvergenceHover
          })
        );
        layers.push(
          new PathLayer({
            id: 'exercise-threat-defense-convergence',
            data: exerciseThreatDefenseConvergence,
            getPath: d => d.path,
            getColor: [250, 204, 21, 248],
            getWidth: 3,
            widthUnits: 'pixels',
            widthMinPixels: 2,
            widthMaxPixels: 5,
            filled: false,
            capRounded: true,
            jointRounded: true,
            pickable: false,
            getDashArray: [1.85, 0.55],
            extensions: [new PathStyleExtension({ dash: true, highPrecisionDash: true })],
            onHover: onConvergenceHover
          })
        );
      }

      if (exerciseOpAlphaPulseRows.length > 0) {
        layers.push(
          new PulsingThreatLayer({
            id: 'exercise-op-alpha-proximity-pulse',
            data: exerciseOpAlphaPulseRows,
            visible: true,
            pulsing: layerAnimationEnabled.threats !== false && isPlaying,
            radiusMode: 'screen',
            ringOnly: true
          })
        );
      }
      /** Exercise USV / vessel head icons are appended after fleet layers (see end of deckLayers) so clicks hit USVs, not underlying fleet dots. */
    }

    // NEW: Interactive Polyline Track Layer. Use per-vessel tracks so the blue path follows each vessel
    // (when no selection, vesselDataSource is all points in time order and would mix vessels).
    if (mapLayerVisibility.track) {
      let trackData = null;
      if (selectedVesselId && vesselDataSource && vesselDataSource.length > 1) {
        trackData = vesselDataSource;
      } else if (!selectedVesselId && clusteredVesselData && clusteredVesselData.length > 0) {
        const segments = buildTrackSegmentsFromTracks(clusteredVesselData);
        trackData = segments.length > 0 ? segments : null;
      }
      if (trackData) {
        const trackLayer = generateTrackLayer(trackData, {
          id: 'vessel-track-layer',
          visible: true,
          onHover: ({ object, x, y }) => {
            if (object) {
              // Optional hover effect
            }
          },
          onClick: () => { /* Vessel/track info shown on hover tooltip only; no click popup */ }
        });
        if (trackLayer) layers.push(trackLayer);
      }
    }

    // Step 7: Marker Clusters view – same clustering + vessel icons/colors as Vessel Tracking
    if (viewMode === 'Marker Clusters' && rawData && rawData.length > 0) {
      const markerClustersDotData = rawData
        .map(d => ({
          ...d,
          lon: d.lon ?? d.longitude,
          lat: d.lat ?? d.latitude
        }))
        .filter(d => d.lon != null && d.lat != null && !isNaN(d.lon) && !isNaN(d.lat));

      if (markerClustersDotData.length > 0) {
        let clusterBounds = null;
        if (dataBounds && typeof dataBounds.west === 'number') {
          clusterBounds = [dataBounds.west, dataBounds.south, dataBounds.east, dataBounds.north];
        } else if (dataBounds && typeof dataBounds.minLon === 'number') {
          clusterBounds = [dataBounds.minLon, dataBounds.minLat, dataBounds.maxLon, dataBounds.maxLat];
        }

        const clusterLayers = generateMarkerClusterLayer(markerClustersDotData, {
          id: `marker-clusters-${fleetMarkerMode}`,
          visible: true,
          zoom: viewState.zoom,
          bounds: clusterBounds,
          useDotMarkers: fleetMarkerMode === 'dots',
          getColor: d => [...getVesselMapDotRgb(d), 230],
          demoRasterVesselIcons: {
            sizePixels: vesselIconPixelSizeForMapZoom(viewState.zoom),
            threatSizePixels: vesselThreatIconPixelSizeForMapZoom(viewState.zoom),
            getKind: (d) => {
              if (isDefensiveSurfaceVesselCategory(d)) return 'defensive';
              if (isDemoAuvRasterVessel(d)) return 'auv';
              if (isDemoThreatMissileVessel(d)) return 'threat';
              return 'friendly';
            }
          },
          onHover: ({ object, x, y }) => {
            if (object) {
              if (object.cluster) {
                setHoveredStation({
                  name: 'Cluster',
                  details: `Count: ${object.point_count}\nThreats: ${object.hasThreat ? 'Yes' : 'No'}`,
                  x, y, isCluster: true
                });
              } else {
                setHoveredStation({
                  name: 'Vessel',
                  details: `Speed: ${object.speed_knots ?? object.speed ?? 0} kts\nHeading: ${object.heading_deg ?? object.direction ?? 0}°\nStatus: ${getVesselStatusLabel(object)}${object.radar_reflectivity_db != null ? `\nReflectivity: ${object.radar_reflectivity_db} dB` : ''}`,
                  x, y, isVessel: true
                });
              }
            } else {
              setHoveredStation(null);
            }
          },
          onClick: (info) => {
            if (info.object && info.object.cluster) {
              const expansionZoom = Math.min(
                info.object.expansion_zoom || viewState.zoom + 1,
                20
              );
              setViewState({
                ...viewState,
                zoom: expansionZoom,
                longitude: info.coordinate[0],
                latitude: info.coordinate[1],
                transitionDuration: 500
              });
            } else if (info.object && !info.object.cluster_id) {
              const o = info.object;
              const norm = { ...o, lon: o.lon ?? o.longitude, lat: o.lat ?? o.latitude };
              setHoveredVesselPoint(norm);
              setHoveredVesselTooltip({ object: norm, x: info.x, y: info.y });
            }
          }
        });
        if (clusterLayers) layers.push(...clusterLayers);
      }
    }

    // Vessel Tracking view: vessel dots at current playback time (Step 2)
    // When we have vesselStates (interpolated at playbackTime), show one dot per vessel at that time.
    // Otherwise fall back to full track dots or latest-only dots depending on layer toggles.
    if (viewMode === 'Vessel Tracking' && vesselDataSource && vesselDataSource.length > 0) {
      const hasPlaybackPositions = vesselStates && vesselStates.length > 0;
      const dotData = hasPlaybackPositions
        ? vesselStates
        : mapLayerVisibility.track
          ? vesselDataSource.filter(d =>
              d.lat != null && d.lon != null && !isNaN(d.lat) && !isNaN(d.lon)
            )
          : mapLayerVisibility.latestVessels && clusteredVesselData.length > 0
            ? clusteredVesselData
                .map(track => track[track.length - 1])
                .filter(p => p && p.lat != null && p.lon != null && !isNaN(p.lat) && !isNaN(p.lon))
            : [];

      const showDots = mapLayerVisibility.track || mapLayerVisibility.latestVessels;
      // Clusters only below max zoom; at max zoom show all vessels (no cluster circles), as before Step 4
      const useClustering = viewState.zoom <= VESSEL_CLUSTER_MAX_ZOOM && viewState.zoom < MAP_MAX_ZOOM;

      // Step 4: cluster circles when zoom < MAP_MAX_ZOOM; at MAP_MAX_ZOOM → individual vessel icons (original logic)
      if (showDots && dotData.length > 0) {
        if (useClustering) {
          let clusterBounds = null;
          if (dataBounds && typeof dataBounds.west === 'number') {
            clusterBounds = [dataBounds.west, dataBounds.south, dataBounds.east, dataBounds.north];
          } else if (dataBounds && typeof dataBounds.minLon === 'number') {
            clusterBounds = [dataBounds.minLon, dataBounds.minLat, dataBounds.maxLon, dataBounds.maxLat];
          }
          const clusterLayers = generateMarkerClusterLayer(dotData, {
            id: `vessel-tracking-clusters-${fleetMarkerMode}`,
            visible: true,
            zoom: viewState.zoom,
            bounds: clusterBounds,
            useDotMarkers: fleetMarkerMode === 'dots',
            getColor: d => [...getVesselMapDotRgb(d), 230],
            demoRasterVesselIcons: {
              sizePixels: vesselIconPixelSizeForMapZoom(viewState.zoom),
              threatSizePixels: vesselThreatIconPixelSizeForMapZoom(viewState.zoom),
              getKind: (d) => {
                if (isDefensiveSurfaceVesselCategory(d)) return 'defensive';
                if (isDemoAuvRasterVessel(d)) return 'auv';
                if (isDemoThreatMissileVessel(d)) return 'threat';
                return 'friendly';
              }
            },
            onHover: () => {},
            onClick: (info) => {
              if (info.isCluster && info.coordinate) {
                const expansionZoom = Math.min(info.expansionZoom ?? viewState.zoom + 1, 20);
                setViewState({
                  ...viewState,
                  zoom: expansionZoom,
                  longitude: info.coordinate[0],
                  latitude: info.coordinate[1],
                  transitionDuration: 500
                });
              } else if (info.object && !info.object.cluster_id) {
                const o = info.object;
                const norm = { ...o, lon: o.lon ?? o.longitude, lat: o.lat ?? o.latitude };
                setHoveredVesselPoint(norm);
                setHoveredVesselTooltip({ object: norm, x: info.x, y: info.y });
              }
            }
          });
          if (clusterLayers && clusterLayers.length > 0) layers.push(...clusterLayers);
        } else {
          const threatHeads = dotData.filter(d => {
            const isT = d.threat_detected === 'Yes' || d.threat_detected === true;
            const lat = d.lat ?? d.latitude;
            const lon = d.lon ?? d.longitude;
            return isT && lat != null && lon != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lon));
          });
          if (threatHeads.length > 0) {
            suppressGeographicThreatPulse = true;
            layers.push(
              new PulsingThreatLayer({
                id: 'vessel-tracking-threat-pulse',
                data: threatHeads,
                visible: true,
                pulsing: layerAnimationEnabled.threats !== false && isPlaying,
                radiusMode: 'screen'
              })
            );
          }
          const dotHasMapPos = (d) => {
            const lat = d.lat ?? d.latitude;
            const lon = d.lon ?? d.longitude;
            return lat != null && lon != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lon));
          };
          const vesselDotsDefense = dotData.filter(
            (d) => dotHasMapPos(d) && isDefensiveSurfaceVesselCategory(d)
          );
          const vesselDotsThreat = dotData.filter(
            (d) => dotHasMapPos(d) && isDemoThreatMissileVessel(d)
          );
          const vesselDotsAuvRaster = dotData.filter(
            (d) => dotHasMapPos(d) && isDemoAuvRasterVessel(d)
          );
          const vesselDotsFriendly = dotData.filter(
            (d) =>
              dotHasMapPos(d) &&
              !isDefensiveSurfaceVesselCategory(d) &&
              !isDemoThreatMissileVessel(d) &&
              !isDemoAuvRasterVessel(d)
          );
          const iconPxZ = vesselIconPixelSizeForMapZoom(viewState.zoom);
          const threatPxZ = vesselThreatIconPixelSizeForMapZoom(viewState.zoom);
          const dotRadiusZ = vesselDotRadiusPixelsForMapZoom(viewState.zoom);
          if (fleetMarkerMode === 'dots') {
            const allDots = dotData.filter(dotHasMapPos);
            if (allDots.length > 0) {
              layers.push(
                new ScatterplotLayer({
                  id: 'vessel-tracking-unclustered-dots',
                  data: allDots,
                  pickable: true,
                  filled: true,
                  stroked: true,
                  radiusUnits: 'pixels',
                  radiusScale: 1,
                  getRadius: dotRadiusZ,
                  getPosition: (d) => [d.lon ?? d.longitude, d.lat ?? d.latitude],
                  getFillColor: (d) => [...getVesselMapDotRgb(d), 230],
                  getLineColor: [255, 255, 255],
                  lineWidthMinPixels: 1,
                  onClick: (info) => {
                    if (info.object) {
                      const o = info.object;
                      const norm = { ...o, lon: o.lon ?? o.longitude, lat: o.lat ?? o.latitude };
                      setHoveredVesselPoint(norm);
                      setHoveredVesselTooltip({ object: norm, x: info.x, y: info.y });
                    }
                  }
                })
              );
            }
          } else {
            layers.push(
              ...buildDemoVesselIconLayers({
                idPrefix: 'vessel-dots-layer',
                threatData: vesselDotsThreat,
                defensiveData: vesselDotsDefense,
                auvData: vesselDotsAuvRaster,
                friendlyData: vesselDotsFriendly,
                getPosition: (d) => [d.lon ?? d.longitude, d.lat ?? d.latitude],
                sizePixels: iconPxZ,
                threatSizePixels: threatPxZ,
                pickable: true,
                onClick: (info) => {
                  if (info.object) {
                    const o = info.object;
                    const norm = { ...o, lon: o.lon ?? o.longitude, lat: o.lat ?? o.latitude };
                    setHoveredVesselPoint(norm);
                    setHoveredVesselTooltip({ object: norm, x: info.x, y: info.y });
                  }
                }
              })
            );
          }
        }
      }

      // Highlighted ring for selected vessel (click in cluster or icon view)
      if (hoveredVesselPoint && hoveredVesselPoint.lat != null && hoveredVesselPoint.lon != null) {
        const hoverColor = getVesselMapDotRgb(hoveredVesselPoint);
        layers.push(new ScatterplotLayer({
          id: 'vessel-dots-hover',
          data: [hoveredVesselPoint],
          pickable: false,
          filled: true,
          stroked: true,
          radiusUnits: 'pixels',
          radiusScale: 1,
          radiusMinPixels: 24,
          radiusMaxPixels: 48,
          getPosition: d => [d.lon, d.lat],
          getRadius: 32,
          getFillColor: () => [...hoverColor, 80],
          getLineColor: () => [...hoverColor, 255],
          lineWidthMinPixels: 4
        }));
      }

      // Velocity/direction arrows: show at all zoom levels when layer is on
      const dotDataWithPath = clusteredVesselData && clusteredVesselData.length > 0
        ? dotData.filter(d => {
            const tid = d.trackId;
            if (tid == null || tid < 0 || !clusteredVesselData[tid]) return false;
            return vesselHasTravelPath(clusteredVesselData[tid]);
          })
        : [];
      const vesselArrowData = generateVesselVelocityArrowData(dotDataWithPath, { arrowLengthScale: 0.035, zoom: viewState.zoom });
      const showVelocityArrows = mapLayerVisibility.velocityArrows !== false;
      if (showDots && vesselArrowData.length > 0 && showVelocityArrows) {
        layers.push(new LineLayer({
          id: 'vessel-velocity-arrows',
          data: vesselArrowData,
          getSourcePosition: d => d.sourcePosition,
          getTargetPosition: d => d.targetPosition,
          getColor: d => d.color,
          getWidth: 2,
          widthMinPixels: 1.5,
          widthMaxPixels: 4,
          pickable: false
        }));
        layers.push(new IconLayer({
          id: 'vessel-velocity-arrow-heads',
          data: vesselArrowData,
          iconAtlas: ARROW_ICON_ATLAS,
          iconMapping: ARROW_ICON_MAPPING,
          getIcon: () => 'arrow',
          getPosition: d => d.targetPosition,
          getSize: 14,
          sizeMinPixels: 8,
          sizeMaxPixels: 24,
          getAngle: d => {
            const deg = d.angleDeg != null ? d.angleDeg : 90 - (d.heading_deg ?? 0);
            return (deg - 90 + 360) % 360;
          },
          getColor: d => d.color,
          billboard: false,
          pickable: false
        }));
      }
    }

    // Threat Zone Overlay (full Threat Zone view, or overlay when "Threat zones" layer is on in Vessel Tracking)
    const showThreatZoneOverlay = viewMode === 'Threat Zone' || (viewMode === 'Vessel Tracking' && mapLayerVisibility.threatZones);
    if (showThreatZoneOverlay && dataForThreatLayers && dataForThreatLayers.length > 0) {
      layers.push(new ThreatZoneOverlay({
        id: 'threat-zone-overlay',
        data: dataForThreatLayers,
        visible: true
      }));
    }

    // Pulsing threat rings (map-wide meters). Skipped when Vessel Tracking shows per-head screen pulses.
    if (
      mapLayerVisibility.threats &&
      dataForThreatLayers &&
      dataForThreatLayers.length > 0 &&
      !suppressGeographicThreatPulse
    ) {
      layers.push(
        new PulsingThreatLayer({
          id: 'pulsing-threat-indicators',
          data: dataForThreatLayers,
          visible: mapLayerVisibility.threats,
          pulsing: threatPulseActive,
          radiusMode: 'geographic'
        })
      );
    }

    // NEW: Animated Vessel Layer (Playback Mode) – keep this for Relative Grid View only
    if (viewMode === 'Relative Grid View' && vesselStates) {
      const vesselLayer = generateVesselLayer(vesselStates, {
        defaultSizePixels: 24,
        threatSizePixels: 34
      });
      if (vesselLayer) {
        if (Array.isArray(vesselLayer)) layers.push(...vesselLayer);
        else layers.push(vesselLayer);
      }
    }

    // Trailing Path Layer (Relative Grid View only)
    if (viewMode === 'Relative Grid View' && mapLayerVisibility.vesselTrail && clusteredVesselData.length > 0) {
      const trailLayers = generateTrailingPathLayer(clusteredVesselData, playbackTime, true);
      if (trailLayers) {
        if (Array.isArray(trailLayers)) layers.push(...trailLayers);
        else layers.push(trailLayers);
      }
    }

    // Exercise scenario USV / scripted vessel icons — must render after Vessel Tracking fleet layers
    // so deck.gl picking hits the USV IconLayer, not the red fleet dot/icon at the same lat/lon.
    if (mapLayerVisibility.exerciseScenario) {
      const normalizeExerciseIconClick = (info) => {
        if (!info?.object) return;
        let o = info.object;
        if (o.tooltipRow) {
          const r = o.tooltipRow;
          o = { ...r, lon: r.lon ?? r.longitude, lat: r.lat ?? r.latitude };
        } else if (Array.isArray(o.position) && o.position.length >= 2) {
          o = { ...o, lon: o.position[0], lat: o.position[1] };
        }
        const norm =
          o.lon != null && o.lat != null && !Number.isNaN(Number(o.lon)) && !Number.isNaN(Number(o.lat))
            ? o
            : { ...o, lon: o.longitude, lat: o.latitude };
        setHoveredVesselPoint(norm);
        setHoveredVesselTooltip({ object: norm, x: info.x, y: info.y });
      };

      if (exerciseHeadsForDots.length > 0) {
        const exerciseDotRadiusZ = vesselDotRadiusPixelsForMapZoom(viewState.zoom);
        if (fleetMarkerMode === 'dots') {
          layers.push(
            new ScatterplotLayer({
              id: 'exercise-vessel-heads-dots',
              data: exerciseHeadsForDots,
              pickable: true,
              filled: true,
              stroked: true,
              radiusUnits: 'pixels',
              radiusScale: 1,
              getRadius: exerciseDotRadiusZ,
              getPosition: (d) => d.position,
              getFillColor: (d) => [...getVesselMapDotRgb(d.tooltipRow || d), 230],
              getLineColor: [255, 255, 255],
              lineWidthMinPixels: 1,
              onClick: normalizeExerciseIconClick
            })
          );
        } else {
          const rowForHead = (h) => h.tooltipRow || h;
          const exerciseHeadsAuvRaster = exerciseHeadsForDots.filter((h) =>
            isDemoAuvRasterVessel(rowForHead(h))
          );
          const exerciseHeadsThreat = exerciseHeadsForDots.filter(
            (h) =>
              !isDemoAuvRasterVessel(rowForHead(h)) && isDemoThreatMissileVessel(rowForHead(h))
          );
          const exerciseHeadsDefense = exerciseHeadsForDots.filter(
            (h) =>
              !isDemoAuvRasterVessel(rowForHead(h)) &&
              !isDemoThreatMissileVessel(rowForHead(h)) &&
              isExerciseDefenseHeadOrPath(h)
          );
          const exerciseHeadsFriendly = exerciseHeadsForDots.filter(
            (h) =>
              !isDemoAuvRasterVessel(rowForHead(h)) &&
              !isDemoThreatMissileVessel(rowForHead(h)) &&
              !isExerciseDefenseHeadOrPath(h)
          );
          layers.push(
            ...buildDemoVesselIconLayers({
              idPrefix: 'exercise-vessel-heads',
              threatData: exerciseHeadsThreat,
              defensiveData: exerciseHeadsDefense,
              auvData: exerciseHeadsAuvRaster,
              friendlyData: exerciseHeadsFriendly,
              getPosition: (d) => d.position,
              getHeadingRow: (d) => d.tooltipRow || d,
              sizePixels: vesselIconPixelSizeForMapZoom(viewState.zoom),
              threatSizePixels: vesselThreatIconPixelSizeForMapZoom(viewState.zoom),
              pickable: true,
              onClick: normalizeExerciseIconClick
            })
          );
        }
      }

      if (exerciseOpAlphaPulseRows.length > 0) {
        const opAlphaDotRadiusZ = vesselDotRadiusPixelsForMapZoom(viewState.zoom);
        if (fleetMarkerMode === 'dots') {
          layers.push(
            new ScatterplotLayer({
              id: 'exercise-op-alpha-proximity-threat-dots',
              data: exerciseOpAlphaPulseRows,
              pickable: true,
              filled: true,
              stroked: true,
              radiusUnits: 'pixels',
              radiusScale: 1,
              getRadius: opAlphaDotRadiusZ,
              getPosition: (d) => [d.lon, d.lat],
              getFillColor: (d) => [...getVesselMapDotRgb(d), 230],
              getLineColor: [255, 255, 255],
              lineWidthMinPixels: 1,
              onClick: normalizeExerciseIconClick
            })
          );
        } else {
          layers.push(
            ...buildDemoVesselIconLayers({
              idPrefix: 'exercise-op-alpha-proximity-threat-core',
              threatData: exerciseOpAlphaPulseRows.filter((r) => isDemoThreatMissileVessel(r)),
              defensiveData: [],
              auvData: exerciseOpAlphaPulseRows.filter((r) => isDemoAuvRasterVessel(r)),
              friendlyData: exerciseOpAlphaPulseRows.filter(
                (r) => !isDemoThreatMissileVessel(r) && !isDemoAuvRasterVessel(r)
              ),
              getPosition: (d) => [d.lon, d.lat],
              sizePixels: vesselIconPixelSizeForMapZoom(viewState.zoom),
              threatSizePixels: vesselThreatIconPixelSizeForMapZoom(viewState.zoom),
              pickable: true,
              onClick: normalizeExerciseIconClick
            })
          );
        }
      }
    }

    return layers;
  }, [
    animationValues,
    clusteredVesselData,
    currentFrame,
    dataBounds,
    dataForThreatLayers,
    exerciseHeadsForDots,
    exerciseOpAlphaPulseRows,
    exerciseThreatDefenseConvergence,
    exerciseVesselDeckData,
    generateGridData,
    gridOpacity,
    heatmapScale,
    isPlaying,
    layerAnimationEnabled,
    mapLayerVisibility,
    oceanCurrentData,
    playbackTime,
    pressureHeatmapData,
    rawData,
    salinityHeatmapData,
    selectedVesselId,
    showGrid,
    sshHexagonData,
    temperatureHeatmapData,
    vesselDataSource,
    vesselStates,
    vesselMarkerDisplay,
    viewMode,
    viewState,
    windSourceData,
    hoveredVesselPoint,
    MAP_MAX_ZOOM
  ]); // eslint-disable-line react-hooks/exhaustive-deps -- deck layer builder reads many props; list curated to avoid rebuild/hover loops

  // Throttle coordinate hover to avoid "Maximum update depth exceeded" from rapid onHover calls
  const handleCoordinateHover = useCallback((info) => {
    if (!info.coordinate) {
      setCoordinateHover((prev) => (prev == null ? prev : null));
      coordinateHoverSigRef.current = '';
      return;
    }
    if (info.object || info.layer?.id?.includes('wind') || info.layer?.id?.includes('grid') || info.layer?.id?.includes('pov') || info.layer?.id?.includes('ssh')) {
      setCoordinateHover((prev) => (prev == null ? prev : null));
      coordinateHoverSigRef.current = '';
      return;
    }
    const now = Date.now();
    if (now - coordinateHoverLastRef.current < 150) return;
    coordinateHoverLastRef.current = now;

    const [longitude, latitude] = info.coordinate;
    const availableData = checkDataAvailability(longitude, latitude);

    if (availableData.length > 0 && viewState.zoom > 6 && viewState.zoom <= 9) {
      const sig = `${latitude.toFixed(4)},${longitude.toFixed(4)}|${availableData.join('|')}`;
      if (coordinateHoverSigRef.current === sig) return;
      coordinateHoverSigRef.current = sig;
      setCoordinateHover({
        name: `${latitude.toFixed(4)}°, ${longitude.toFixed(4)}°`,
        details: `Available data:\n${availableData.join('\n')}`,
        x: info.x,
        y: info.y,
        isCoordinate: true,
        longitude,
        latitude
      });
    } else {
      coordinateHoverSigRef.current = '';
      setCoordinateHover((prev) => (prev == null ? prev : null));
    }
  }, [viewState.zoom, checkDataAvailability]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0f172a]">
      {viewMode === 'Threat Zone' && (
        <div className="absolute bottom-8 left-2 bg-white/90 dark:bg-slate-900/90 border border-red-300 p-2 rounded z-20 pointer-events-none shadow-lg dark:bg-slate-900/90 dark:border-red-500/50">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500/40 border border-red-500 rounded-sm animate-pulse"></div>
            <span className="text-red-400 font-semibold text-xs uppercase tracking-wider">Threat Zone</span>
          </div>
        </div>
      )}
      <div ref={el => { mapContainerRef.current = el; if (el && !mapContainerReady) setMapContainerReady(true); }} className="absolute inset-0 w-full h-full" />


      {mapContainerReady && <DeckGL
        viewState={viewState}
        onViewStateChange={({ viewState: vs }) => {
          const clampedZoom = Math.min(MAP_MAX_ZOOM, vs.zoom);
          const clamped = { ...vs, zoom: clampedZoom };
          setViewState(clamped);
          if (mapRef.current) {
            mapRef.current.jumpTo({
              center: [clamped.longitude, clamped.latitude],
              zoom: clamped.zoom,
              pitch: clamped.pitch,
              bearing: clamped.bearing
            }, { duration: 0 });
          }
        }}
        controller={true}
        layers={deckLayers}
        onHover={handleCoordinateHover}
        onClick={(info) => {
          const lid = info.layer?.id;
          const isVesselLayer = isVesselPickLayerId(lid);
          if (info.object && isVesselLayer) {
            let o = info.object;
            if (o.tooltipRow) {
              const r = o.tooltipRow;
              o = { ...r, lon: r.lon ?? r.longitude, lat: r.lat ?? r.latitude };
            }
            const norm = o.lon != null ? o : { ...o, lon: o.longitude, lat: o.latitude };
            setHoveredVesselPoint(norm);
            setHoveredVesselTooltip({ object: norm, x: info.x, y: info.y });
          } else {
            setHoveredVesselPoint(null);
            setHoveredVesselTooltip(null);
            if (!info.object) setTrackInfo(null);
          }
        }}
        className="absolute inset-0 w-full h-full z-10"
      />}

      {commsBurst && commsBurstLayoutPx && (() => {
        const L = commsBurst;
        const lay = commsBurstLayoutPx;
        const fadeOut = L.phase === 'out';
        const phaseLabel =
          L.msg.narrativePhase &&
          ({
            Before: 'Before threat approaches',
            During: 'Threat in op area',
            After: 'After threat passes'
          }[L.msg.narrativePhase] || L.msg.narrativePhase);
        const mapAria = [
          'Exercise comms link',
          phaseLabel,
          `${L.msg.from} to ${L.msg.to}`,
          'Full message text is in the Exercise comms sidebar.'
        ]
          .filter(Boolean)
          .join('. ');
        const dot = (x, y, key) => (
          <div
            key={key}
            className="absolute flex items-center justify-center"
            style={{ left: x, top: y, transform: 'translate(-50%, -50%)', zIndex: 3 }}
          >
            <span
              className={`absolute inline-flex h-5 w-5 rounded-full opacity-35${isPlaying ? ' animate-ping' : ''}`}
              style={{ backgroundColor: lay.strokeMain }}
              aria-hidden
            />
            <span
              className="relative inline-flex h-2.5 w-2.5 rounded-full border-2 border-white shadow dark:border-slate-900"
              style={{ backgroundColor: lay.strokeMain }}
              aria-hidden
            />
          </div>
        );
        return (
          <div
            className={`absolute inset-0 pointer-events-none z-[22] overflow-hidden transition-opacity duration-300 ease-out ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
            role="img"
            aria-label={mapAria}
          >
            <svg className="absolute inset-0 h-full w-full overflow-hidden" aria-hidden>
              <line
                x1={lay.x1}
                y1={lay.y1}
                x2={lay.x2}
                y2={lay.y2}
                stroke={lay.strokeSoft}
                strokeWidth={5}
                strokeLinecap="round"
              />
              <line
                x1={lay.x1}
                y1={lay.y1}
                x2={lay.x2}
                y2={lay.y2}
                className={`scenario-comms-link-trace${isPlaying ? '' : ' scenario-comms-link-trace--paused'}`}
                stroke={lay.strokeMain}
                strokeWidth={2}
                strokeLinecap="round"
                fill="none"
              />
            </svg>
            {dot(lay.x1, lay.y1, 'sc-from')}
            {dot(lay.x2, lay.y2, 'sc-to')}
          </div>
        );
      })()}

      <div className="absolute top-2 md:top-2 left-2 md:left-4 bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 rounded-lg p-2 z-20 max-h-96 overflow-y-auto shadow-lg dark:bg-slate-800/90 dark:border-slate-600/50">
        <div className="flex justify-between items-center mb-2">
          <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 dark:text-slate-300">Global Controls</div>
          <button onClick={() => setShowMapControls(!showMapControls)} className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 dark:text-slate-400 dark:hover:text-slate-200">{showMapControls ? '∧' : '+'}</button>
        </div>

        {showMapControls && (
          <>
            <div className="mb-3">
              <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1 dark:text-slate-400">Map Style</label>
              <select value={mapStyle} onChange={(e) => handleMapStyleChange(e.target.value)} className="w-full text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-slate-700 dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200">
                <option value="arcgis-ocean">🌊 Ocean (ArcGIS)</option>
                <option value="mapbox://styles/mapbox/outdoors-v11">🏞️ Outdoors</option>
                <option value="mapbox://styles/mapbox/satellite-v9">🛰️ Satellite</option>
                <option value="mapbox://styles/mapbox/dark-v10">🌑 Dark</option>
                <option value="mapbox://styles/mapbox/light-v10">💡 Light</option>
                <option value="mapbox://styles/mapbox/streets-v9">🛣️ Streets</option>
              </select>
            </div>
            <div className="mb-3 pb-2 border-b border-slate-200 dark:border-slate-700 dark:border-slate-600">
              <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 dark:text-slate-300">Globe Controls</div>
              <div className="flex items-center space-x-2 mb-2">
                <button onClick={() => { setSpinEnabled(!spinEnabled); if (!spinEnabled) spinGlobe(); }} className={`w-4 h-4 rounded border ${spinEnabled ? 'bg-blue-500 border-blue-500' : 'bg-transparent border-slate-500'}`}>{spinEnabled && <svg className="w-3 h-3 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</button>
                <span className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-400">Auto Rotate Globe</span>
              </div>
              <button onClick={() => mapRef.current?.easeTo({ center: [0, 20], zoom: 1.5, pitch: 0, bearing: 0, duration: 2000 })} className="w-full text-xs bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-2 py-1 rounded mb-2 dark:bg-slate-600 dark:hover:bg-slate-500 dark:text-slate-200">🌍 Global View</button>
            </div>
            <div className="mb-3 pb-2 border-b border-slate-200 dark:border-slate-700 dark:border-slate-600">
              <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 dark:text-slate-300">Coordinate Grid</div>
              <div className="flex items-center space-x-2 mb-2">
                <button onClick={() => setShowGrid(!showGrid)} className={`w-4 h-4 rounded border ${showGrid ? 'bg-blue-500 border-blue-500' : 'bg-transparent border-slate-500'}`}>{showGrid && <svg className="w-3 h-3 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</button>
                <span className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-400">📋 Lat/Lon Grid</span>
              </div>
              {showGrid && <div className="ml-4 space-y-2 dark:bg-slate-600 dark:text-slate-400"><div><label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Opacity: {Math.round(gridOpacity * 100)}%</label><input type="range" min="0.1" max="1" step="0.1" value={gridOpacity} onChange={(e) => setGridOpacity(parseFloat(e.target.value))} className="w-full h-1 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer" /></div><div><label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Grid Spacing: {gridSpacing}°</label><input type="range" min="1" max="30" step="1" value={gridSpacing} onChange={(e) => setGridSpacing(parseInt(e.target.value))} className="w-full h-1 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer" /></div></div>}
            </div>
          </>
        )}
      </div>

      <div className="absolute top-2 md:top-4 right-9 md:right-11 bg-white/80 dark:bg-slate-900/80 px-2 md:px-3 py-1 md:py-2 rounded-lg pointer-events-none z-20 shadow-sm dark:bg-slate-800/80">
        <div className="text-xs md:text-sm font-mono">
          Frame: {totalFrames > 0 ? `${currentFrame + 1}/${totalFrames}` : '— (no timeline)'}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-400">{selectedArea}</div>
        {currentDate && currentTime && <div className="text-xs text-green-600 dark:text-green-300 mt-1 dark:text-green-300">{currentDate} {currentTime}</div>}
      </div>

      <div className="absolute bottom-5 md:bottom-7 left-2 md:left-4 bg-white/80 dark:bg-slate-900/80 px-2 md:px-3 py-1 md:py-2 rounded-lg pointer-events-none z-20 max-w-xs shadow-sm dark:bg-slate-800/80">
        <div className="text-xs md:text-sm font-semibold text-slate-600 dark:text-slate-300 dark:text-slate-300">Interactive Ocean Map</div>
        {(!rawData || rawData.length === 0) && viewState.zoom <= 9 && (
          <div className="text-xs font-bold text-red-600 dark:text-red-500 mt-1 mb-1">No data returned from API.</div>
        )}
        <div className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-400">Depth: {selectedDepth}m</div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 dark:text-slate-400">

        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 dark:text-slate-400">
          {mapLayerVisibility.oceanCurrents && <span className="text-blue-600 dark:text-blue-400 dark:text-blue-300">🌊 Currents </span>}
          {mapLayerVisibility.temperature && <span className="text-red-600 dark:text-red-400 dark:text-red-300">🌡️ Temperature </span>}
          {mapLayerVisibility.salinity && <span className="text-emerald-600 dark:text-emerald-300 dark:text-emerald-300">🧂 Salinity </span>}
          {mapLayerVisibility.ssh && <span className="text-indigo-600 dark:text-indigo-300">🌊 SSH </span>}
          {mapLayerVisibility.pressure && <span className="text-orange-600 dark:text-orange-300">⚖️ Pressure </span>}
          {mapLayerVisibility.windVelocity && <span className="text-yellow-600 dark:text-yellow-300">💨 Wind Velocity </span>}
          {(showGrid || viewMode === 'Relative Grid View') && <span className="text-blue-600 dark:text-blue-400 dark:text-blue-300">📋 Grid </span>}
          {mapStyle === 'arcgis-ocean' && viewMode !== 'Relative Grid View' && <span className="text-indigo-600 dark:text-indigo-300">🌊 Ocean Base </span>}
        </div>
        {viewMode === 'Vessel Tracking' && (mapLayerVisibility.latestVessels || mapLayerVisibility.track || mapLayerVisibility.velocityArrows || mapLayerVisibility.threatZones) && (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-2 pt-2 border-t border-slate-200 dark:border-slate-600 dark:text-slate-400">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {(mapLayerVisibility.latestVessels || mapLayerVisibility.track) && (
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 dark:bg-green-400" aria-hidden />
                  <span>Vessel positions</span>
                </span>
              )}
              {mapLayerVisibility.velocityArrows && (
                <span className="inline-flex items-center gap-1">
                  <span className="text-indigo-500 dark:text-indigo-400">→</span>
                  <span>Direction arrows</span>
                </span>
              )}
              {mapLayerVisibility.threatZones && (
                <span className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500 dark:bg-red-400" aria-hidden />
                  <span>Threat zones</span>
                </span>
              )}
            </div>
          </div>
        )}
        {spinEnabled && <div className="text-xs text-cyan-600 dark:text-cyan-300 mt-1 dark:text-cyan-300">🌍 Globe Auto-Rotating</div>}
        {pauseParticleAnimations && <div className="text-xs text-red-600 dark:text-red-400 mt-1 dark:text-red-300">⏸️ Particles Paused</div>}
      </div>

      <StationTooltip station={activeTooltip} />

      {viewMode === 'Relative Grid View' && vesselStates && vesselStates.length > 0 && (
        <VesselHUD
          speed={vesselStates[0].speed_knots}
          heading={vesselStates[0].heading_deg}
          threatDetected={vesselStates[0].threat_detected}
          timestamp={vesselStates[0].timestamp}
          radarReflectivity={vesselStates[0].radar_reflectivity_db}
        />
      )}

      <SelectedStationPanel station={selectedStation} data={rawData} onClose={() => { setSelectedStation(null); onStationSelect?.(null); }} />

      {/* Vessel popup: icon click only (not track/path hover) */}
      {hoveredVesselTooltip && (() => {
        const obj = hoveredVesselTooltip.object;
        let popupX = hoveredVesselTooltip.x;
        let popupY = hoveredVesselTooltip.y;
        if (mapRef.current && obj?.lat != null && obj?.lon != null) {
          try {
            const pt = mapRef.current.project([obj.lon, obj.lat]);
            if (pt && typeof pt.x === 'number' && typeof pt.y === 'number') {
              popupX = pt.x;
              popupY = pt.y;
            }
          } catch (_) { /* keep click position */ }
        }
        return (
        <div
          className="absolute bg-white/95 dark:bg-slate-800/95 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-lg z-50 text-xs text-slate-700 dark:text-slate-200 dark:bg-slate-900/95 dark:border-yellow-500/50 dark:text-slate-200"
          style={{
            left: popupX,
            top: popupY,
            transform: 'translate(-50%, -100%)',
            marginTop: '-10px',
            minWidth: '200px'
          }}
        >
          <div className="flex justify-between items-start gap-2 mb-2 border-b border-slate-700/50 pb-1">
            <div className="flex items-center gap-2 min-w-0">
              {React.createElement(getVesselIconComponent(getVesselCategoryRaw(hoveredVesselTooltip.object)), {
                className: `w-5 h-5 flex-shrink-0 ${getVesselStatusTextClass(hoveredVesselTooltip.object).replace('font-semibold', '').trim()}`,
                'aria-hidden': true
              })}
              <span className={`font-semibold truncate ${getVesselStatusTextClass(hoveredVesselTooltip.object)}`}>{getVesselCategoryName(hoveredVesselTooltip.object)}</span>
            </div>
          </div>
          <div className="space-y-1 font-mono">
            <div>
              <span className="text-slate-400">Vessel type:</span> {getVesselCategoryName(hoveredVesselTooltip.object)}
            </div>
            <div className={getVesselStatusTextClass(hoveredVesselTooltip.object)}>
              <span className="text-slate-400 font-normal">Status:</span> {getVesselStatusLabel(hoveredVesselTooltip.object)}
            </div>
            {(hoveredVesselTooltip.object.unique_identifier != null && hoveredVesselTooltip.object.unique_identifier !== '') && (
              <div>
                <span className="text-slate-400">Vessel ID:</span> {hoveredVesselTooltip.object.unique_identifier}
              </div>
            )}
            <div><span className="text-slate-400">Time:</span> {hoveredVesselTooltip.object.time || hoveredVesselTooltip.object.timestamp}</div>
            <div><span className="text-slate-400">Speed:</span> {(hoveredVesselTooltip.object.speed ?? hoveredVesselTooltip.object.speed_knots) ?? 0} knots</div>
            <div><span className="text-slate-400">Heading:</span> {hoveredVesselTooltip.object.direction ?? hoveredVesselTooltip.object.heading_deg ?? 0}°</div>
            <div><span className="text-slate-400">Lat/Lon:</span> {hoveredVesselTooltip.object.lat?.toFixed(4)}, {hoveredVesselTooltip.object.lon?.toFixed(4)}</div>
            <div><span className="text-slate-400">Depth:</span> {(() => { const v = hoveredVesselTooltip.object.depth ?? hoveredVesselTooltip.object.depth_m; const n = Number(v); return (v != null && v !== '' && !Number.isNaN(n)) ? `${n} m` : 'NaN'; })()}</div>
            <div><span className="text-slate-400">Salinity:</span> {(() => {
              const o = hoveredVesselTooltip.object;
              const v = o.salinity ?? o.water_salinity_psu;
              const n = Number(v);
              return (v != null && v !== '' && !Number.isNaN(n)) ? `${n.toFixed(2)} PSU` : '—';
            })()}</div>
            <div><span className="text-slate-400">Temperature:</span> {(() => {
              const o = hoveredVesselTooltip.object;
              const v = o.temp ?? o.temperature ?? o.water_temperature_c;
              const n = Number(v);
              return (v != null && v !== '' && !Number.isNaN(n)) ? `${n.toFixed(2)}°C` : '—';
            })()}</div>
            {hoveredVesselTooltip.object.radar_reflectivity_db != null && (
              <div><span className="text-slate-400">Reflectivity:</span> {hoveredVesselTooltip.object.radar_reflectivity_db} dB</div>
            )}
          </div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-slate-900/95"></div>
        </div>
        );
      })()}

    </div>
  );
};

export default MapContainer;